/*global Dingdocs*/

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { initView } from 'dingtalk-docs-cool-app';
import { 
  Typography, 
  Button, 
  Card, 
  Upload, 
  Radio, 
  Checkbox, 
  Input, 
  Table, 
  Progress,
  Select,
  Space
} from 'dingtalk-design-desktop';
import { parseExcelFile, predictFieldTypes, isFirstRowHeader, type ParsedExcelData, type FieldTypePrediction } from '../utils/excelParser';
import { downloadDingTalkFile, type DingTalkFile } from '../utils/dingtalkFilePicker';
import { DingTalkFileBrowser } from './DingTalkFileBrowser';
import './style.css';

interface Sheet {
  id: string;
  name: string;
  fieldsCount: number;
}

interface Field {
  id: string;
  name: string;
  type: string;
}

interface View {
  id: string;
  name: string;
  type: string;
}

type ImportMode = 'create' | 'append' | 'updateBlank' | 'updateExisting' | 'updateBoth';

interface FieldMapping {
  targetField: string; // 目标数据表字段名
  sourceField: string; // 导入文件字段名，空字符串表示不导入
  isNewField: boolean; // 是否为新增字段
  isFuzzyMatch?: boolean; // 是否为模糊匹配（用于高亮提醒）
}

function App() {
  // 全局捕获未处理的Promise错误，避免"Uncaught (in promise)"错误
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // 检查是否是超时错误
      const error = event.reason;
      const errorMessage = error?.message || String(error || '未知错误');
      
      // 更严格的超时错误判断
      const errorMessageLower = errorMessage.toLowerCase();
      const isRealTimeoutError = (
        errorMessageLower === 'timeout' ||
        errorMessageLower === '超时' ||
        errorMessageLower.includes('timeout error') ||
        errorMessageLower.includes('超时错误') ||
        errorMessageLower.includes('execution timeout') ||
        errorMessageLower.includes('请求超时') ||
        errorMessageLower.includes('操作超时') ||
        (errorMessageLower.includes('timeout') && (
          errorMessageLower.includes('exceeded') ||
          errorMessageLower.includes('expired') ||
          errorMessageLower.includes('timed out')
        ))
      );
      
      // 记录详细的错误信息
      console.error('[未捕获的Promise错误] 检测到未处理的Promise错误:');
      console.error(`  - 错误消息: "${errorMessage}"`);
      console.error(`  - 错误对象:`, error);
      console.error(`  - 错误堆栈:`, error?.stack || '无堆栈信息');
      console.error(`  - 是否在导入过程中: ${isImportingRef.current}`);
      console.error(`  - 是否判定为超时错误: ${isRealTimeoutError}`);
      
      // 如果是真正的超时错误且正在导入，设置取消标志
      if (isRealTimeoutError && isImportingRef.current) {
        console.error('[未捕获的Promise错误] 检测到超时错误，设置取消标志');
        console.log(`[错误详情] 错误消息: "${errorMessage}"`);
        isImportCancelledRef.current = true;
        setLoading(false);
        setImportStatus('导入超时，已停止');
        isImportingRef.current = false;
        // 阻止默认的错误处理（避免在控制台显示红色错误）
        event.preventDefault();
        return;
      } else if (isRealTimeoutError && !isImportingRef.current) {
        // 初始化阶段的超时，只记录警告，不设置取消标志
        console.warn('[未捕获的Promise错误] 检测到超时错误（初始化阶段），不设置取消标志');
        console.warn(`[错误详情] 错误消息: "${errorMessage}"`);
        // 不阻止错误处理，让浏览器正常显示（但不会设置取消标志）
      } else if (errorMessageLower.includes('timeout') || errorMessageLower.includes('超时')) {
        // 如果包含timeout但不是真正的超时错误，只记录警告
        console.warn(`[未捕获的Promise错误] 错误消息包含"timeout"但可能不是真正的超时错误: ${errorMessage}`);
      }
      
      // 其他未捕获的Promise错误，记录但不阻止（让浏览器正常显示）
      console.warn('[未捕获的Promise错误] 其他类型的错误:', error);
    };
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  
  // 全局拦截钉钉日志上报请求，避免403错误干扰调试
  // 这个请求是钉钉内部系统的日志上报功能，不是业务代码的一部分
  useEffect(() => {
    // 拦截 fetch 请求
    const originalFetch = window.fetch;
    (window as any).fetch = function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      const url = typeof input === 'string' ? input : input instanceof Request ? input.url : input.toString();
      
      // 如果是钉钉日志上报请求，静默处理，避免403错误干扰
      // 覆盖所有钉钉日志上报域名
      if (url.includes('.log.aliyuncs.com') && (
        url.includes('lippi-doc-we-collab') ||
        url.includes('lippi-doc-portal-frontend') ||
        url.includes('lippi-doc-notable-frontend') ||
        url.includes('/logstores/') ||
        url.includes('/track?')
      )) {
        // 静默处理，返回一个模拟成功的响应
        // 不输出日志，避免控制台噪音
        return Promise.resolve(new Response(null, {
          status: 200,
          statusText: 'OK',
          headers: new Headers({
            'Content-Type': 'text/plain;charset=UTF-8'
          })
        })).catch(() => {
          // 即使出错也静默处理，不抛出异常
          return new Response(null, { status: 200, statusText: 'OK' });
        });
      }
      
      // 其他请求正常处理
      return originalFetch.call(this, input, init);
    };

    // 拦截 XMLHttpRequest（如果钉钉使用XHR发送日志）
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;
    
    XMLHttpRequest.prototype.open = function(method: string, url: string | URL, ...rest: any[]) {
      const urlStr = typeof url === 'string' ? url : url.toString();
      if (urlStr.includes('.log.aliyuncs.com') && (
        urlStr.includes('lippi-doc-we-collab') ||
        urlStr.includes('lippi-doc-portal-frontend') ||
        urlStr.includes('lippi-doc-notable-frontend') ||
        urlStr.includes('/logstores/') ||
        urlStr.includes('/track?')
      )) {
        // 标记为日志请求，后续在send中处理
        (this as any)._isLogRequest = true;
      }
      return originalXHROpen.apply(this, [method, url, ...rest] as any);
    };
    
    XMLHttpRequest.prototype.send = function(body?: Document | XMLHttpRequestBodyInit | null) {
      if ((this as any)._isLogRequest) {
        // 静默处理日志请求，模拟成功响应
        // 使用 Object.defineProperty 来设置只读属性（如果可能）
        try {
          setTimeout(() => {
            // 触发 onload 事件（如果已设置）
            if (this.onload) {
              try {
                this.onload(new ProgressEvent('load') as any);
              } catch (e) {
                // 忽略错误
              }
            }
            // 触发 onreadystatechange 事件（如果已设置）
            if (this.onreadystatechange) {
              try {
                // 使用 Object.defineProperty 临时修改只读属性
                Object.defineProperty(this, 'readyState', { value: 4, writable: true, configurable: true });
                Object.defineProperty(this, 'status', { value: 200, writable: true, configurable: true });
                Object.defineProperty(this, 'statusText', { value: 'OK', writable: true, configurable: true });
                this.onreadystatechange(new ProgressEvent('readystatechange') as any);
              } catch (e) {
                // 忽略错误
              }
            }
          }, 0);
        } catch (e) {
          // 如果设置失败，静默忽略
        }
        return;
      }
      return originalXHRSend.call(this, body);
    };

    // 清理函数：恢复原始方法（虽然通常不需要）
    return () => {
      (window as any).fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalXHROpen;
      XMLHttpRequest.prototype.send = originalXHRSend;
    };
  }, []); // 只在组件挂载时执行一次

  const [loading, setLoading] = useState<boolean>(false);
  const [activeSheet, setActiveSheet] = useState<Sheet | null>(null);
  const [allSheets, setAllSheets] = useState<Sheet[]>([]);
  const [selectedSheetId, setSelectedSheetId] = useState<string>('');
  // 导入取消标志（使用 ref 用于循环检查，避免闭包问题）
  const isImportCancelledRef = useRef<boolean>(false);
  // 导入进行中标志（用于区分初始化和导入过程）
  const isImportingRef = useRef<boolean>(false);
  const [fields, setFields] = useState<Field[]>([]);
  const [views, setViews] = useState<View[]>([]);
  const [selectedViewId, setSelectedViewId] = useState<string>('');
  
  // 导入来源选择
  type ImportSource = 'local' | 'dingtalk';
  const [importSource, setImportSource] = useState<ImportSource>('local');
  
  // 文件相关状态
  const [file, setFile] = useState<File | null>(null);
  const [dingtalkFile, setDingtalkFile] = useState<DingTalkFile | null>(null);
  const [selectingDingtalkFile, setSelectingDingtalkFile] = useState<boolean>(false);
  const [showFileBrowser, setShowFileBrowser] = useState<boolean>(false);
  const [parsedData, setParsedData] = useState<ParsedExcelData | null>(null);
  const [previewRows, setPreviewRows] = useState<any[]>([]);
  const [fieldPredictions, setFieldPredictions] = useState<FieldTypePrediction[]>([]);
  
  // 导入配置
  const [firstRowIsHeader, setFirstRowIsHeader] = useState<boolean>(true);
  const [autoAddFields, setAutoAddFields] = useState<boolean>(true);
  const [ignoreErrors, setIgnoreErrors] = useState<boolean>(false);
  const [enableFuzzyMatch, setEnableFuzzyMatch] = useState<boolean>(true); // 是否启用字段模糊匹配，默认启用
  const [importMode, setImportMode] = useState<ImportMode>('create');
  const [newSheetName, setNewSheetName] = useState<string>('');
  const [matchFields, setMatchFields] = useState<string[]>([]);
  const [selectedFields, setSelectedFields] = useState<Record<string, string>>({});
  
  // 字段映射相关状态
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [showFieldMapping, setShowFieldMapping] = useState<boolean>(false);
  
  // 汇总导入相关状态
  const [enableAggregation, setEnableAggregation] = useState<boolean>(false); // 是否汇总导入，默认不汇总
  const [aggregationFields, setAggregationFields] = useState<string[]>([]); // 分组字段（1-3个）
  const [sumFields, setSumFields] = useState<string[]>([]); // 需要求和的数值字段
  const [textSeparator, setTextSeparator] = useState<string>('；'); // 文本合并分隔符，默认分号
  
  // 重复数据处理策略（当未启用汇总导入时，如果检测到重复数据）
  const [enableDuplicateMerge, setEnableDuplicateMerge] = useState<boolean>(false); // 是否合并重复数据，默认不处理
  const [duplicateMergeStrategy, setDuplicateMergeStrategy] = useState<'mostComplete' | 'earliest' | 'latest' | 'mostRecentEdit' | 'oldestEdit' | 'largestField' | 'smallestField'>('mostComplete');
  const [duplicateMergeField, setDuplicateMergeField] = useState<string>(''); // 用于 largestField/smallestField 策略的自定义字段
  
  // 断点续传
  const [startChunk, setStartChunk] = useState<number>(1); // 从第几个分段开始导入（1-based）
  
  // 导入进度
  const [importProgress, setImportProgress] = useState<number>(0);
  const [importStatus, setImportStatus] = useState<string>('');
  
  // 查询进度状态
  const [queryProgress, setQueryProgress] = useState<{
    isQuerying: boolean;
    currentQuery: number;
    totalRecords: number;
    totalRecordsEstimate: number;
    matchedKeys: number;
    targetKeys: number;
    status: string;
    sheetName?: string;
  }>({
    isQuerying: false,
    currentQuery: 0,
    totalRecords: 0,
    totalRecordsEstimate: 0,
    matchedKeys: 0,
    targetKeys: 0,
    status: '',
    sheetName: undefined
  });
  
  // 重复数据详情状态
  const [duplicateData, setDuplicateData] = useState<{
    sheetName: string;
    queryFields: Array<{ name: string; type: string }>;
    totalRecords: number;
    totalQueries: number;
    duplicateGroups: Array<{
      matchKey: string;
      records: Array<{
        id: string;
        fields: Record<string, any>;
        isBestRecord: boolean;
      }>;
      bestRecordId: string;
    }>;
    duplicateCount: number;
  } | null>(null);
  
  // 大数据量处理配置
  const [chunkSize, setChunkSize] = useState<number>(1000); // 自定义分片大小（默认1000行/片）
  
  // 分片进度
  const [currentChunk, setCurrentChunk] = useState<number>(0); // 当前处理的分片索引
  const [totalChunks, setTotalChunks] = useState<number>(0); // 总分片数
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [chunkProgress, setChunkProgress] = useState<{ current: number; total: number; success: number; failed: number }>({
    current: 0,
    total: 0,
    success: 0,
    failed: 0
  });
  
  // 性能监控
  const performanceMetricsRef = useRef<{
    startTime: number;
    stages: Record<string, { start: number; end?: number; duration?: number }>;
    memoryUsage: Array<{ time: number; used: number; total: number }>;
    networkRequests: Array<{ time: number; duration: number; success: boolean; error?: string }>;
  }>({
    startTime: 0,
    stages: {},
    memoryUsage: [],
    networkRequests: []
  });
  
  // 分片重试机制（预留，待实现）
  // const chunkRetryRef = useRef<Map<number, { retries: number; lastError?: string }>>(new Map());
  // const MAX_CHUNK_RETRIES = 3; // 每个分片最多重试3次
  
  // 日志捕获
  const [logs, setLogs] = useState<string[]>([]);
  // 仅错误行日志（来自后端的逐行错误）
  const [errorLogLines, setErrorLogLines] = useState<string[]>([]);
  const logBuffer = useRef<string[]>([]);
  const logContainerRef = useRef<HTMLDivElement>(null);
  
  // 检查页面是否可见（避免在 visibilitychange 事件期间显示 alert）
  const isPageVisible = useCallback(() => {
    return !document.hidden && document.visibilityState === 'visible';
  }, []);
  
  // 配置权限（企业内部应用和ISV应用都需要调用此方法）
  // 官方文档：https://open.dingtalk.com/document/isvapp/jsapi-authentication
  // 说明：企业内发布插件或者ISV三方插件，都需要调用此方法，才允许使用 script api 读写 AI 表格数据
  const configurePermission = useCallback(async () => {
    const verboseAuthLog = false; // 默认关闭签名服务的详细控制台输出
    const authLog = (...args: any[]) => {
      if (verboseAuthLog) console.log(...args);
    };
    try {
      authLog('====================================');
      authLog('→ 开始配置权限 (configPermission)');
      authLog('====================================');
      
      // 1. 检查 Dingdocs SDK 是否可用
      if (typeof (window as any).Dingdocs === 'undefined') {
        throw new Error('Dingdocs SDK 未加载，请检查 script.html 是否正确加载');
      }
      
      if (!(window as any).Dingdocs.base?.host?.configPermission) {
        throw new Error('Dingdocs.base.host.configPermission 方法不可用');
      }
      
      // 2. 获取当前页面URL（不包含hash和query参数）
      // 重要：URL 必须是完整的页面地址，用于签名验证
      const currentUrl = window.location.href.split('#')[0].split('?')[0];
      authLog('  当前页面URL:', currentUrl);
      
      // 3. 调用后端签名服务获取签名参数
      // 签名服务会根据 URL 生成符合钉钉要求的签名
      authLog('  → 请求后端签名服务...');
      const response = await fetch('/api/dingtalk/signature', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: currentUrl })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`签名服务返回错误 (HTTP ${response.status}): ${errorText}`);
      }
      
      const signatureData = await response.json();
      
      // 4. 验证签名参数完整性
      if (!signatureData.agentId || !signatureData.corpId || 
          !signatureData.timeStamp || !signatureData.nonceStr || !signatureData.signature) {
        throw new Error('签名参数不完整: ' + JSON.stringify(signatureData));
      }
      
      authLog('  ✓ 签名参数获取成功');
      authLog('    AgentId:', signatureData.agentId);
      authLog('    CorpId:', signatureData.corpId);
      authLog('    TimeStamp:', signatureData.timeStamp);
      authLog('    NonceStr:', signatureData.nonceStr);
      authLog('    Signature:', signatureData.signature.substring(0, 16) + '...');
      
      // 5. 调用 configPermission 进行权限配置
      // 官方API定义：
      // configPermission: (
      //   agentId: string,      // 应用的 AgentId（从开放平台获取）
      //   corpId: string,       // 企业ID（从开放平台获取）
      //   timeStamp: number,    // 时间戳（毫秒）
      //   nonceStr: string,     // 随机字符串
      //   signature: string,    // 签名（SHA256）
      //   jsApiList: string[],  // 需要的权限列表
      // ) => Promise<void>;
      authLog('  → 调用 configPermission...');
      
      // 必需的权限：DingdocsScript.base.readWriteAll
      // 该权限允许插件通过 script api 读写 AI 表格数据
      const jsApiList = ['DingdocsScript.base.readWriteAll'];
      
      await (window as any).Dingdocs.base.host.configPermission(
        signatureData.agentId,
        signatureData.corpId,
        signatureData.timeStamp,
        signatureData.nonceStr,
        signatureData.signature,
        jsApiList
      );
      
      authLog('  ✓ 权限配置成功');
      authLog('    授权权限:', jsApiList);
      authLog('====================================');
      return true;
    } catch (error: any) {
      console.error('====================================');
      console.error('✗ 权限配置失败');
      console.error('====================================');
      console.error('错误信息:', error.message);
      // 隐藏签名细节，仅在需要时打开 verboseAuthLog
      if (verboseAuthLog) {
      console.error('错误堆栈:', error.stack);
      }
      
      // 构建详细的错误提示
      let errorMessage = `权限配置失败: ${error.message}\n\n`;
      
      // 根据错误类型提供具体的解决方案
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorMessage += '【网络错误】\n';
        errorMessage += '可能原因：\n';
        errorMessage += '1. 后端签名服务未启动\n';
        errorMessage += '2. Nginx API代理未配置或配置错误\n';
        errorMessage += '3. 网络连接问题\n\n';
        errorMessage += '解决方法：\n';
        errorMessage += '生产环境：\n';
        errorMessage += '- 检查签名服务: ssh root@服务器 "pm2 list"\n';
        errorMessage += '- 启动签名服务: ssh root@服务器 "pm2 start /opt/dingtalk-signature-service/签名服务.js --name dingtalk-signature"\n';
        errorMessage += '- 检查 Nginx 配置: /etc/nginx/conf.d/002-plugin.conf 中是否配置了 location /api/\n';
        errorMessage += '本地开发：\n';
        errorMessage += '- 启动签名服务: cd backend && npm start\n';
        errorMessage += '- 检查服务状态: curl http://localhost:3001/api/health\n';
      } else if (error.message.includes('HTTP 4') || error.message.includes('HTTP 5')) {
        errorMessage += '【服务器错误】\n';
        errorMessage += '可能原因：\n';
        errorMessage += '1. 应用凭证配置错误\n';
        errorMessage += '2. AppKey/AppSecret 不正确\n';
        errorMessage += '3. AgentId/CorpId 不匹配\n\n';
        errorMessage += '解决方法：\n';
        errorMessage += '- 检查 backend/.env 文件配置\n';
        errorMessage += '- 登录钉钉开放平台：https://open-dev.dingtalk.com\n';
        errorMessage += '- 进入应用详情 → 凭证与基础信息\n';
        errorMessage += '- 确认以下信息与 .env 文件一致：\n';
        errorMessage += '  * AgentId (DINGTALK_AGENT_ID)\n';
        errorMessage += '  * AppKey (DINGTALK_APP_KEY)\n';
        errorMessage += '  * AppSecret (DINGTALK_APP_SECRET)\n';
        errorMessage += '  * CorpId (DINGTALK_CORP_ID)\n';
      } else if (error.message.includes('configPermission') || error.message.includes('PermissionError')) {
        errorMessage += '【权限配置调用错误】\n';
        errorMessage += '可能原因：\n';
        errorMessage += '1. Dingdocs SDK 未正确加载\n';
        errorMessage += '2. 签名参数格式错误\n';
        errorMessage += '3. 应用权限未在开放平台申请\n';
        errorMessage += '4. 权限未生效（需要切换到正式版）\n\n';
        errorMessage += '解决方法：\n';
        errorMessage += '【检查 SDK 加载】\n';
        errorMessage += '- 确认 script.html 正确引用了 Dingdocs SDK\n';
        errorMessage += '【申请应用权限】（最重要）\n';
        errorMessage += '- 登录钉钉开放平台：https://open-dev.dingtalk.com\n';
        errorMessage += '- 进入应用详情 → 权限管理\n';
        errorMessage += '- 切换到「正式版」（不是开发版）\n';
        errorMessage += '- 添加以下权限：\n';
        errorMessage += '  * Notable.Base.Read.All (读取AI表格数据)\n';
        errorMessage += '  * Notable.Base.Write.All (写入AI表格数据)\n';
        errorMessage += '- 确认权限状态为「已生效」\n';
        errorMessage += '【企业授权】\n';
        errorMessage += '- 企业管理员进入「工作台管理」→「应用管理」\n';
        errorMessage += '- 确认应用可见范围包含当前用户\n';
      }
      
      errorMessage += '\n详细信息请查看浏览器控制台';
      
      throw new Error(errorMessage);
    }
  }, []);
  
  // 开始捕获日志
  const startLogCapture = useCallback((): (() => void) => {
    setLogs([]);
    logBuffer.current = [];
    
    // 保存原始console方法
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;
    
    // 重写console.log
    console.log = (...args: any[]) => {
      originalLog.apply(console, args);
      const logStr = args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ');
      const logEntry = `[LOG] ${new Date().toLocaleTimeString()} ${logStr}`;
      logBuffer.current.push(logEntry);
      // 实时更新日志状态，限制最多保留最近500条日志
      if (logBuffer.current.length > 500) {
        logBuffer.current.shift();
      }
      // 实时更新UI显示的日志（异步更新，避免阻塞）
      setTimeout(() => {
        setLogs([...logBuffer.current]);
        // 自动滚动到底部，显示最新日志
        if (logContainerRef.current) {
          logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
      }, 0);
      
      // 解析查询进度日志并实时更新状态
      if (logStr.includes('[查询进度]')) {
        // 解析格式1：[查询进度] 第 X 次查询完成，已查询 Y 条记录，找到 Z/A 个匹配键
        const queryMatch = logStr.match(/\[查询进度\]\s*第\s*(\d+)\s*次查询完成[，,]\s*已查询\s*(\d+)\s*条记录[，,]\s*找到\s*(\d+)\/(\d+)\s*个匹配键/);
        if (queryMatch) {
          const currentQuery = parseInt(queryMatch[1], 10);
          const totalRecords = parseInt(queryMatch[2], 10);
          const matchedKeys = parseInt(queryMatch[3], 10);
          const targetKeys = parseInt(queryMatch[4], 10);
          
          // 直接更新状态，确保实时更新
          setQueryProgress({
            isQuerying: true,
            currentQuery,
            totalRecords,
            totalRecordsEstimate: totalRecords,
            matchedKeys,
            targetKeys,
            status: `正在查询数据表... 第 ${currentQuery} 次查询，已查询 ${totalRecords} 条记录，找到 ${matchedKeys}/${targetKeys} 个匹配键`
          });
        } else {
          // 解析格式2：[查询进度] 查询完成，共查询 X 条记录，总记录数 Y 条（查询完成时）
          const completeMatch = logStr.match(/\[查询进度\]\s*查询完成[，,]\s*共查询\s*(\d+)\s*条记录[，,]\s*总记录数\s*(\d+)\s*条/);
          if (completeMatch) {
            const totalRecords = parseInt(completeMatch[1], 10);
            const totalRecordsEstimate = parseInt(completeMatch[2], 10);
            
            // 尝试提取数据表名称
            const sheetNameMatch = logStr.match(/数据表"([^"]+)"/);
            const sheetName = sheetNameMatch ? sheetNameMatch[1] : undefined;
            
            // 强制更新状态，确保UI刷新
            setQueryProgress(prev => ({
              isQuerying: false,
              currentQuery: prev.currentQuery,
              totalRecords: totalRecords,
              totalRecordsEstimate: totalRecordsEstimate,
              matchedKeys: 0,
              targetKeys: 0,
              sheetName: sheetName || prev.sheetName,
              status: sheetName 
                ? `查询完成，数据表"${sheetName}"共 ${totalRecordsEstimate} 条记录`
                : `查询完成，共查询 ${totalRecordsEstimate} 条记录`
            }));
          } else {
            // 解析格式3：[查询进度] 第 X 次查询完成，已查询 Y 条记录，查询中...（查询过程中）
            const queryingMatch = logStr.match(/\[查询进度\]\s*第\s*(\d+)\s*次查询完成[，,]\s*已查询\s*(\d+)\s*条记录[，,]\s*查询中\.\.\./);
            if (queryingMatch) {
              const currentQuery = parseInt(queryingMatch[1], 10);
              const totalRecords = parseInt(queryingMatch[2], 10);
              
              // 尝试提取数据表名称
              const sheetNameMatch = logStr.match(/数据表"([^"]+)"/);
              const sheetName = sheetNameMatch ? sheetNameMatch[1] : undefined;
              
              // 强制更新状态，确保UI刷新
              setQueryProgress(prev => ({
                isQuerying: true,
                currentQuery,
                totalRecords,
                totalRecordsEstimate: 0, // 查询中，总记录数未知
                matchedKeys: 0,
                targetKeys: 0,
                sheetName: sheetName || prev.sheetName,
                status: sheetName 
                  ? `正在查询数据表"${sheetName}"... 第 ${currentQuery} 次查询，已查询 ${totalRecords} 条记录，查询中...`
                  : `正在查询数据表... 第 ${currentQuery} 次查询，已查询 ${totalRecords} 条记录，查询中...`
              }));
            } else {
              // 解析格式4a：[查询进度] 数据表"xxx"视图中的总记录数: X 条（获取总记录数）
              const totalCountMatch = logStr.match(/\[查询进度\]\s*数据表"([^"]+)"视图中的总记录数:\s*(\d+)\s*条/);
              if (totalCountMatch) {
                const sheetName = totalCountMatch[1];
                const totalRecordsEstimate = parseInt(totalCountMatch[2], 10);
                
                // 更新总记录数，保持其他状态不变
                setQueryProgress(prev => ({
                  ...prev,
                  totalRecordsEstimate,
                  sheetName: sheetName || prev.sheetName,
                  status: prev.status || `正在查询数据表"${sheetName}"中的重复数据...`
                }));
              } else {
                // 解析格式4b：[查询进度] 第 X 次查询完成，已查询 Y 条记录，总记录数 Z 条（用于数据表去重）
                // 支持格式：[查询进度] 第 X 次查询完成，已查询 Y 条记录，总记录数 Z 条（视图ID: xxx）
                const oldFormatMatch = logStr.match(/\[查询进度\]\s*第\s*(\d+)\s*次查询完成[，,]\s*已查询\s*(\d+)\s*条记录[，,]\s*总记录数\s*(\d+)\s*条/);
                if (oldFormatMatch) {
                  const currentQuery = parseInt(oldFormatMatch[1], 10);
                  const totalRecords = parseInt(oldFormatMatch[2], 10);
                  const totalRecordsEstimate = parseInt(oldFormatMatch[3], 10);
                  
                  // 尝试提取数据表名称
                  const sheetNameMatch = logStr.match(/数据表"([^"]+)"/);
                  const sheetName = sheetNameMatch ? sheetNameMatch[1] : undefined;
                  
                  // 强制更新状态，确保UI刷新（使用完整对象，不使用prev）
                  const newProgress = {
                    isQuerying: true,
                    currentQuery,
                    totalRecords,
                    totalRecordsEstimate,
                    matchedKeys: 0,
                    targetKeys: 0,
                    sheetName: sheetName || undefined,
                    status: sheetName 
                      ? `正在查询数据表"${sheetName}"... 第 ${currentQuery} 次查询，已查询 ${totalRecords} 条记录，总记录数 ${totalRecordsEstimate} 条`
                      : `正在查询数据表... 第 ${currentQuery} 次查询，已查询 ${totalRecords} 条记录，总记录数 ${totalRecordsEstimate} 条`
                  };
                  
                  // 直接更新状态，确保实时更新
                  setQueryProgress(newProgress);
                } else {
                  // 解析格式5：[查询进度] 第 X 次查询完成，已查询 Y 条记录（无总记录数）
                  // 支持格式：[查询进度] 第 X 次查询完成，已查询 Y 条记录（视图ID: xxx）
                  // 注意：X 对应后端的 queryCount，Y 对应后端的 allRecords.length
                  // 日志格式示例：[查询进度] 第 48 次查询完成, 已查询 24000 条记录 (视图ID: wuvfw0G)
                  const basicQueryMatch = logStr.match(/\[查询进度\]\s*第\s*(\d+)\s*次查询完成[，,]\s*已查询\s*(\d+)\s*条记录/);
                  if (basicQueryMatch) {
                    // queryCount: 从日志中解析出的查询次数（对应后端的 queryCount 变量）
                    const queryCount = parseInt(basicQueryMatch[1], 10);
                    // allRecordsLength: 从日志中解析出的已查询记录数（对应后端的 allRecords.length 变量）
                    const allRecordsLength = parseInt(basicQueryMatch[2], 10);
                    
                    // 调试日志：确认解析成功
                    console.log(`[日志解析] 格式5匹配成功: queryCount=${queryCount}, allRecordsLength=${allRecordsLength}`);
                    
                    // 尝试提取数据表名称
                    const sheetNameMatch = logStr.match(/数据表"([^"]+)"/);
                    const sheetName = sheetNameMatch ? sheetNameMatch[1] : undefined;
                    
                    // 使用完整对象更新，确保UI刷新
                    // currentQuery 对应后端的 queryCount，totalRecords 对应后端的 allRecords.length
                    const newProgress = {
                      isQuerying: true,
                      currentQuery: queryCount, // 对应后端的 queryCount
                      totalRecords: allRecordsLength, // 对应后端的 allRecords.length
                      totalRecordsEstimate: 0,
                      matchedKeys: 0,
                      targetKeys: 0,
                      sheetName: sheetName || undefined,
                      status: sheetName
                        ? `正在查询数据表"${sheetName}"... 第 ${queryCount} 次查询，已查询 ${allRecordsLength} 条记录`
                        : `正在查询数据表... 第 ${queryCount} 次查询，已查询 ${allRecordsLength} 条记录`
                    };
                    
                    // 直接更新状态，不使用setTimeout，确保实时更新
                    setQueryProgress(newProgress);
                    console.log(`[状态更新] 已更新 queryProgress: currentQuery=${queryCount}, totalRecords=${allRecordsLength}`);
                  } else {
                    // 调试日志：如果格式5不匹配，输出日志内容以便排查
                    console.warn(`[日志解析] 格式5不匹配，日志内容: ${logStr.substring(0, 100)}`);
                  }
                }
              }
            }
          }
        }
      } else if (logStr.includes('[查询进度] 查询完成')) {
        // 数据表去重查询完成
        // 匹配格式：[查询进度] 查询完成，共查询 X 条记录，总记录数 Y 条
        const completeMatch = logStr.match(/\[查询进度\]\s*查询完成[，,]\s*共查询\s*(\d+)\s*条记录[，,]\s*总记录数\s*(\d+)\s*条/);
        if (completeMatch) {
          const totalRecords = parseInt(completeMatch[1], 10);
          const totalRecordsEstimate = parseInt(completeMatch[2], 10);
          setQueryProgress(prev => ({
            ...prev,
            isQuerying: false,
            totalRecords,
            totalRecordsEstimate,
            status: prev.sheetName 
              ? `查询完成，数据表"${prev.sheetName}"共 ${totalRecordsEstimate} 条记录`
              : `查询完成，共查询 ${totalRecordsEstimate} 条记录`
          }));
        } else {
          // 匹配格式：[查询进度] 查询完成，共查询 X 条记录
          const simpleCompleteMatch = logStr.match(/\[查询进度\]\s*查询完成[，,]\s*共查询\s*(\d+)\s*条记录/);
          if (simpleCompleteMatch) {
            const totalRecords = parseInt(simpleCompleteMatch[1], 10);
            setQueryProgress(prev => ({
              ...prev,
              isQuerying: false,
              totalRecords,
              totalRecordsEstimate: totalRecords,
              status: prev.sheetName 
                ? `查询完成，数据表"${prev.sheetName}"共 ${totalRecords} 条记录`
                : `查询完成，共查询 ${totalRecords} 条记录`
            }));
          }
        }
      } else if (logStr.includes('开始查询数据表"')) {
        // 提取数据表名称
        const sheetNameMatch = logStr.match(/数据表"([^"]+)"/);
        if (sheetNameMatch) {
          setQueryProgress(prev => ({
            ...prev,
            sheetName: sheetNameMatch[1],
            isQuerying: true,
            status: `开始查询数据表"${sheetNameMatch[1]}"...`
          }));
        }
      } else if (logStr.includes('[索引构建完成]') || (logStr.includes('[模式') && logStr.includes('索引构建完成'))) {
        // 查询完成，重置查询进度状态
        setQueryProgress(prev => ({
          ...prev,
          isQuerying: false,
          status: '查询完成'
        }));
      } else if (logStr.includes('[查询进度] 开始查询') || logStr.includes('开始分页查询') || logStr.includes('智能索引构建')) {
        // 开始查询
        // 尝试提取数据表名称
        const sheetNameMatch = logStr.match(/数据表"([^"]+)"/);
        const sheetName = sheetNameMatch ? sheetNameMatch[1] : undefined;
        
        setQueryProgress(prev => ({
          isQuerying: true,
          currentQuery: 0,
          totalRecords: 0,
          totalRecordsEstimate: 0,
          matchedKeys: 0,
          targetKeys: 0,
          status: sheetName ? `开始查询数据表"${sheetName}"...` : '开始查询数据表...',
          sheetName: sheetName || prev.sheetName
        }));
      } else if (logStr.includes('开始查询') && !logStr.includes('[查询进度]')) {
        // 兼容其他格式的"开始查询"日志（不包含[查询进度]前缀）
        const sheetNameMatch = logStr.match(/数据表"([^"]+)"/);
        const sheetName = sheetNameMatch ? sheetNameMatch[1] : undefined;
        
        setQueryProgress(prev => ({
          ...prev,
          isQuerying: true,
          currentQuery: 0,
          totalRecords: 0,
          totalRecordsEstimate: 0,
          status: sheetName ? `开始查询数据表"${sheetName}"...` : '开始查询数据表...',
          sheetName: sheetName || prev.sheetName
        }));
      }
    };
    
    // 重写console.error
    console.error = (...args: any[]) => {
      originalError.apply(console, args);
      const logStr = args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ');
      const logEntry = `[ERROR] ${new Date().toLocaleTimeString()} ${logStr}`;
      logBuffer.current.push(logEntry);
      // 实时更新日志状态，限制最多保留最近500条日志
      if (logBuffer.current.length > 500) {
        logBuffer.current.shift();
      }
      // 实时更新UI显示的日志（异步更新，避免阻塞）
      setTimeout(() => {
        setLogs([...logBuffer.current]);
        // 自动滚动到底部，显示最新日志
        if (logContainerRef.current) {
          logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
      }, 0);
    };
    
    // 重写console.warn
    console.warn = (...args: any[]) => {
      originalWarn.apply(console, args);
      const logStr = args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ');
      const logEntry = `[WARN] ${new Date().toLocaleTimeString()} ${logStr}`;
      logBuffer.current.push(logEntry);
      // 实时更新日志状态，限制最多保留最近500条日志
      if (logBuffer.current.length > 500) {
        logBuffer.current.shift();
      }
      // 实时更新UI显示的日志（异步更新，避免阻塞）
      setTimeout(() => {
        setLogs([...logBuffer.current]);
      }, 0);
    };
    
    // 保存恢复函数
    return () => {
      console.log = originalLog;
      console.error = originalError;
      console.warn = originalWarn;
    };
  }, []);
  
  // 停止捕获日志
  const stopLogCapture = useCallback(() => {
    setLogs([...logBuffer.current]);
    logBuffer.current = [];
  }, []);
  
  // 下载日志文件（保留用于未来可能的UI功能）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const downloadLogs = useCallback(() => {
    // 优先使用错误行日志；若无则回退到捕获的控制台日志
    const targetLines = errorLogLines.length > 0 ? errorLogLines : logs;
    const logContent = targetLines.join('\n');
    const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    link.download = `导入日志-${timestamp}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [logs, errorLogLines]);

  // 使用 useMemo 稳定数据表选项，避免不必要的重新渲染
  const sheetOptions = useMemo(() => {
    return allSheets.map(s => ({ label: s.name, value: s.id }));
  }, [allSheets]);

  // 使用 useMemo 稳定字段选项
  const fieldOptions = useMemo(() => {
    return fields.map(f => ({ label: f.name, value: f.name }));
  }, [fields]);

  // 匹配字段选项：只包含已映射的导入字段（sourceField不为空的字段）
  const matchFieldOptions = useMemo(() => {
    if (fieldMappings.length === 0) {
      // 如果没有字段映射，返回所有字段（兼容旧逻辑）
      return fieldOptions;
    }
    
    // 只返回在字段映射中选择了导入的字段（sourceField不为空）
    const mappedFields = fieldMappings
      .filter(mapping => mapping.sourceField && mapping.sourceField !== '')
      .map(mapping => mapping.targetField);
    
    return fieldOptions.filter(option => mappedFields.includes(option.value));
  }, [fieldMappings, fieldOptions]);

  // 加载所有数据表列表
  // 参考000架构：简化错误处理，统一处理权限错误
  const loadAllSheets = useCallback(async () => {
    try {
      console.log('开始加载数据表列表...');
      // 增加超时时间到90秒，因为大数据表可能需要更长时间
      // 注意：初始化阶段的超时不会设置取消标志，允许继续初始化
      const sheets = await scriptRunWithTimeout('getAllSheets', 90);
      console.log('加载的数据表列表:', sheets);
      setAllSheets(sheets || []);
      // 如果列表为空，尝试添加当前激活的数据表
      if ((!sheets || sheets.length === 0) && activeSheet) {
        setAllSheets([activeSheet]);
      }
    } catch (error: any) {
      console.error('Load all sheets failed:', error);
      const errorMsg = error?.message || String(error);
      
      // 检查是否是真正的超时错误（实际执行时间接近设置的超时时间）
      // 如果实际执行时间很短就报超时，可能是其他错误（如网络错误、权限错误等）
      const isTimeoutMsg = errorMsg.toLowerCase().includes('timeout') || errorMsg.includes('超时');
      if (isTimeoutMsg) {
        console.warn('[加载数据表] 检测到超时错误消息，但可能是其他错误被误判为超时');
        console.warn('[加载数据表] 建议检查：1. 网络连接 2. 权限配置 3. 钉钉SDK是否正常加载');
        console.warn('[加载数据表] 如果实际执行时间很短（<1秒），很可能是网络或权限问题，而非真正的超时');
      }
      
      // 检查是否是权限错误
      const isPermissionError = errorMsg.includes('PermissionError') || 
                                errorMsg.includes('permission') || 
                                errorMsg.includes('权限') ||
                                errorMsg.includes('Do not have permission');
      
      if (isPermissionError) {
        // 权限错误，提供详细的解决指引（参考000架构，但保留权限错误的特殊处理）
        const permissionErrorMsg = `权限错误：${errorMsg}\n\n请按以下步骤检查：\n\n` +
          `1. 检查应用权限（最重要）：\n` +
          `   - 登录钉钉开放平台\n` +
          `   - 进入应用详情 → 权限管理\n` +
          `   - 切换到「正式版」（不是开发版）\n` +
          `   - 确认 Notable.Base.Read.All 和 Notable.Base.Write.All 权限状态为「已生效」\n\n` +
          `2. 检查企业授权：\n` +
          `   - 企业管理员进入「工作台管理」→「应用管理」\n` +
          `   - 确认应用可见范围包含当前用户\n\n` +
          `3. 检查文档权限：\n` +
          `   - 确认当前用户对AI表格有「可编辑」权限\n\n` +
          `详细说明请查看：权限配置与问题排查指南`;
        console.error(permissionErrorMsg);
        // 只在页面可见时显示 alert，避免在 visibilitychange 事件期间被阻止
        if (!document.hidden && document.visibilityState === 'visible') {
        alert(permissionErrorMsg);
        } else {
          console.warn('[权限错误] 页面不可见，已跳过 alert 提示');
        }
      } else {
        // 其他错误（参考000架构：简单记录，不阻塞UI）
        console.error('加载数据表列表失败:', errorMsg);
      }
      
      // 如果加载失败，至少显示当前激活的数据表
      if (activeSheet) {
        setAllSheets([activeSheet]);
      }
    }
  }, [activeSheet]);

  // 加载指定数据表的视图列表
  const loadSheetViews = useCallback(async (sheetId: string) => {
    try {
      console.log('开始加载数据表视图，sheetId:', sheetId);
      if (!sheetId) {
        console.error('sheetId为空');
        setViews([]);
        setSelectedViewId('');
        return [];
      }
      // 检查Dingdocs是否可用
      if (typeof Dingdocs === 'undefined' || !Dingdocs.script) {
        const errorMsg = 'Dingdocs SDK未加载，无法获取视图';
        console.error(errorMsg);
        setViews([]);
        setSelectedViewId('');
        return [];
      }
      // 增加超时时间到60秒
      const viewList = await scriptRunWithTimeout('getSheetViews', 60, sheetId);
      console.log('加载的视图列表:', viewList);
      setViews(viewList || []);
      // 默认选择第一个视图
      if (viewList && viewList.length > 0) {
        setSelectedViewId(viewList[0].id);
      } else {
        setSelectedViewId('');
      }
      return viewList || [];
    } catch (error: any) {
      console.error('Load sheet views failed:', error);
      const errorMsg = error?.message || String(error);
      console.error('错误详情:', {
        message: errorMsg,
        stack: error?.stack,
        name: error?.name,
        sheetId
      });
      setViews([]);
      setSelectedViewId('');
      return [];
    }
  }, []);

  // 加载指定数据表的字段
  const loadSheetFields = useCallback(async (sheetId: string) => {
    try {
      console.log('开始加载数据表字段，sheetId:', sheetId);
      if (!sheetId) {
        console.error('sheetId为空');
        setFields([]);
        return [];
      }
      // 检查Dingdocs是否可用
      if (typeof Dingdocs === 'undefined' || !Dingdocs.script) {
        const errorMsg = 'Dingdocs SDK未加载，无法获取字段';
        console.error(errorMsg);
        // 只在页面可见时显示 alert，避免在 visibilitychange 事件期间被阻止
        if (!document.hidden && document.visibilityState === 'visible') {
        alert(errorMsg);
        } else {
          console.warn('[SDK错误] 页面不可见，已跳过 alert 提示');
        }
        setFields([]);
        return [];
      }
      // 增加超时时间到60秒，因为大数据表可能需要更长时间
      const fieldList = await scriptRunWithTimeout('getSheetFields', 60, sheetId);
      console.log('加载的字段列表:', fieldList);
      setFields(fieldList || []);
      return fieldList || [];
    } catch (error: any) {
      console.error('Load sheet fields failed:', error);
      const errorMsg = error?.message || String(error);
      console.error('错误详情:', {
        message: errorMsg,
        stack: error?.stack,
        name: error?.name,
        sheetId
      });
      alert(`加载字段失败: ${errorMsg}\n\n数据表ID: ${sheetId}\n\n请检查：\n1. 数据表是否存在\n2. 是否有读取权限`);
      setFields([]);
      return [];
    }
  }, []);

  // 加载当前数据表信息
  // 参考000架构：简化错误处理，统一在UI层处理权限错误
  const loadActiveSheet = useCallback(async () => {
    try {
      // 增加超时时间到60秒，因为大数据表可能需要更长时间
      const sheet = await scriptRunWithTimeout('getActiveSheet', 60);
      setActiveSheet(sheet);
      if (sheet) {
        setSelectedSheetId(sheet.id);
        // 并行加载字段和视图
        try {
          await Promise.all([
            (async () => {
              const fieldList = await scriptRunWithTimeout('getSheetFields', 60, sheet.id);
              setFields(fieldList);
            })(),
            loadSheetViews(sheet.id)
          ]);
        } catch (err) {
          console.error('加载字段或视图失败:', err);
        }
      }
    } catch (error: any) {
      // 参考000架构：统一错误处理，不在这里特殊处理权限错误
      console.error('Load active sheet failed:', error);
    }
  }, [loadSheetViews]);

  // 生成字段映射（自动匹配）
  const generateFieldMappings = useCallback((targetFields: Field[], sourceFields: string[], useFuzzyMatch: boolean = true) => {
    console.log('\n========== [字段映射] 开始生成字段映射 ==========');
    console.log(`[字段映射] Excel文件字段总数: ${sourceFields.length}`);
    console.log(`[字段映射] Excel文件字段列表:`, sourceFields);
    console.log(`[字段映射] 数据表字段总数: ${targetFields.length}`);
    console.log(`[字段映射] 数据表字段列表:`, targetFields.map(f => ({ name: f.name, type: f.type })));
    
    const mappings: FieldMapping[] = [];
    const matchedSourceFields = new Set<string>(); // 记录已匹配的源字段
    const unmatchedTargetFields: string[] = []; // 记录未匹配的目标字段
    const matchedFields: Array<{ target: string; source: string; matchType: string }> = []; // 记录匹配详情
    
    // 为每个目标字段创建映射（按照targetFields的顺序，确保与数据表字段顺序一致）
    targetFields.forEach(targetField => {
      // 尝试自动匹配：名称相同或相似
      let matchedSource = '';
      const targetName = targetField.name.toLowerCase().trim();
      let matchType = '未匹配';
      
      // 精确匹配
      const exactMatch = sourceFields.find(s => s.toLowerCase().trim() === targetName);
      if (exactMatch) {
        matchedSource = exactMatch;
        matchType = '精确匹配';
        matchedSourceFields.add(exactMatch);
      } else if (useFuzzyMatch) {
        // 模糊匹配：包含关系（仅在启用模糊匹配时执行）
        const fuzzyMatch = sourceFields.find(s => {
          const sourceName = s.toLowerCase().trim();
          return sourceName.includes(targetName) || targetName.includes(sourceName);
        });
        if (fuzzyMatch) {
          matchedSource = fuzzyMatch;
          matchType = '模糊匹配';
          matchedSourceFields.add(fuzzyMatch);
        } else {
          unmatchedTargetFields.push(targetField.name);
        }
      } else {
        // 未启用模糊匹配，且精确匹配失败
        unmatchedTargetFields.push(targetField.name);
      }
      
      if (matchedSource) {
        matchedFields.push({ target: targetField.name, source: matchedSource, matchType });
      }
      
      mappings.push({
        targetField: targetField.name,
        sourceField: matchedSource,
        isNewField: false,
        isFuzzyMatch: matchType === '模糊匹配'
      });
    });
    
    console.log(`[字段映射] 精确匹配字段数: ${matchedFields.filter(m => m.matchType === '精确匹配').length}`);
    console.log(`[字段映射] 模糊匹配字段数: ${matchedFields.filter(m => m.matchType === '模糊匹配').length}`);
    console.log(`[字段映射] 未匹配的目标字段数: ${unmatchedTargetFields.length}`);
    if (unmatchedTargetFields.length > 0 && unmatchedTargetFields.length <= 20) {
      console.log(`[字段映射] 未匹配的目标字段列表:`, unmatchedTargetFields);
    } else if (unmatchedTargetFields.length > 20) {
      console.log(`[字段映射] 未匹配的目标字段列表（前20个）:`, unmatchedTargetFields.slice(0, 20));
    }
    
    // 检查导入文件中有但数据表中没有的字段（需要新增的字段）
    const unmatchedSourceFields: string[] = [];
    sourceFields.forEach(sourceField => {
      const exists = targetFields.some(f => f.name === sourceField);
      if (!exists && autoAddFields) {
        unmatchedSourceFields.push(sourceField);
        mappings.push({
          targetField: sourceField,
          sourceField: sourceField,
          isNewField: true
        });
      } else if (!exists && !autoAddFields) {
        unmatchedSourceFields.push(sourceField);
      }
    });
    
    console.log(`[字段映射] 未匹配的Excel字段数: ${unmatchedSourceFields.length}`);
    if (unmatchedSourceFields.length > 0 && unmatchedSourceFields.length <= 20) {
      console.log(`[字段映射] 未匹配的Excel字段列表:`, unmatchedSourceFields);
    } else if (unmatchedSourceFields.length > 20) {
      console.log(`[字段映射] 未匹配的Excel字段列表（前20个）:`, unmatchedSourceFields.slice(0, 20));
    }
    
    if (autoAddFields) {
      console.log(`[字段映射] 自动添加字段已启用，将创建 ${unmatchedSourceFields.length} 个新字段`);
    } else {
      console.log(`[字段映射] 自动添加字段已禁用，${unmatchedSourceFields.length} 个Excel字段将被忽略`);
    }
    
    // 确保返回的映射按照数据表字段顺序排序
    // 创建字段名到索引的映射，用于排序
    const fieldIndexMap = new Map<string, number>();
    targetFields.forEach((field, index) => {
      fieldIndexMap.set(field.name, index);
    });
    
    // 对映射进行排序：先按数据表字段顺序，然后是新字段
    mappings.sort((a, b) => {
      const aIndex = fieldIndexMap.get(a.targetField);
      const bIndex = fieldIndexMap.get(b.targetField);
      
      // 如果都是数据表字段，按索引排序
      if (aIndex !== undefined && bIndex !== undefined) {
        return aIndex - bIndex;
      }
      // 如果a是数据表字段，b是新字段，a排在前面
      if (aIndex !== undefined && bIndex === undefined) {
        return -1;
      }
      // 如果a是新字段，b是数据表字段，b排在前面
      if (aIndex === undefined && bIndex !== undefined) {
        return 1;
      }
      // 如果都是新字段，按字段名排序
      return a.targetField.localeCompare(b.targetField);
    });
    
    // 统计最终映射结果
    const mappedCount = mappings.filter(m => m.sourceField && m.sourceField !== '').length;
    const unmappedCount = mappings.filter(m => !m.sourceField || m.sourceField === '').length;
    const newFieldCount = mappings.filter(m => m.isNewField).length;
    
    console.log(`[字段映射] 最终生成的映射总数: ${mappings.length}`);
    console.log(`[字段映射] 已映射字段数（sourceField不为空）: ${mappedCount}`);
    console.log(`[字段映射] 未映射字段数（sourceField为空，将不导入）: ${unmappedCount}`);
    console.log(`[字段映射] 新增字段数: ${newFieldCount}`);
    console.log(`========== [字段映射] 字段映射生成完成 ==========\n`);
    
    return mappings;
  }, [autoAddFields]);

  // 处理数据表选择变化 - 同步更新状态
  const handleSheetChange = useCallback(async (sheetId: string) => {
    console.log('选择数据表:', sheetId);
    // 同步更新状态
    setSelectedSheetId(sheetId);
    setMatchFields([]); // 清空匹配字段选择
    // 异步加载字段和视图，不阻塞UI
    if (sheetId) {
      try {
        // 并行加载字段和视图
        const [fieldList] = await Promise.all([
          loadSheetFields(sheetId),
          loadSheetViews(sheetId)
        ]);
        // 字段加载完成后，生成字段映射
        if (parsedData && fieldList.length > 0) {
          const mappings = generateFieldMappings(fieldList, parsedData.headers, enableFuzzyMatch);
          setFieldMappings(mappings);
          setShowFieldMapping(true);
        }
      } catch (err) {
        console.error('加载字段或视图失败:', err);
      }
    } else {
      setFields([]);
      setViews([]);
      setSelectedViewId('');
      setFieldMappings([]);
      setShowFieldMapping(false);
    }
  }, [loadSheetFields, loadSheetViews, parsedData, generateFieldMappings, enableFuzzyMatch]);

  // 初始化
  useEffect(() => {
    console.log('初始化插件...');
    
    // 检查是否在钉钉环境内
    const isInDingTalk = typeof (window as any).DingTalkPC !== 'undefined' || 
                         window.location.href.includes('dingtalk.com') ||
                         window.location.href.includes('alidocs.dingtalk.com');
    
    if (!isInDingTalk && process.env.NODE_ENV === 'development') {
      console.warn('[开发环境] 检测到不在钉钉环境内，某些功能可能无法正常使用');
      console.warn('[开发环境] 建议在钉钉内打开插件进行测试');
    }
    
    initView({
      onReady: async () => {
        console.log('插件已就绪，开始配置权限和加载数据...');
        
        try {
          // 1. 先配置权限（企业内部应用和ISV应用都需要）
          await configurePermission();
          
          // 2. 检查Dingdocs是否可用
          if (typeof Dingdocs === 'undefined') {
            console.error('Dingdocs SDK未加载');
            if (isPageVisible()) {
            alert('Dingdocs SDK未加载，请检查script.html是否正确配置');
            }
            return;
          }
          if (!Dingdocs.script) {
            console.error('Dingdocs.script不可用');
            if (isPageVisible()) {
            alert('Dingdocs.script不可用，请检查script.html是否正确加载');
            }
            return;
          }
        } catch (error: any) {
          console.error('权限配置失败:', error);
          // 只在页面可见时显示 alert，避免在 visibilitychange 事件期间被阻止
          if (isPageVisible()) {
          alert(
            '权限配置失败！\n\n' +
            error.message + '\n\n' +
            '请检查：\n' +
            '1. 后端签名服务是否启动\n' +
            '2. 应用凭证是否配置正确\n' +
            '3. 网络连接是否正常\n\n' +
            '详细信息请查看浏览器控制台'
          );
          } else {
            // 页面不可见时，只记录到控制台，不显示 alert
            console.warn('[权限配置失败] 页面不可见，已跳过 alert 提示');
          }
          return;
        }
        
        // 3. 加载数据表列表和当前数据表（使用更长的超时时间，允许失败但不阻塞）
        try {
        await loadAllSheets();
        } catch (error: any) {
          console.warn('加载数据表列表失败，继续尝试加载当前数据表:', error);
        }
        
        try {
        await loadActiveSheet();
        } catch (error: any) {
          console.warn('加载当前数据表失败:', error);
        }
      },
      onError: (error: any) => {
        console.error('插件初始化错误:', error);
        alert(`插件初始化失败: ${error?.message || String(error)}`);
      }
    });
      }, [configurePermission, loadAllSheets, loadActiveSheet, isPageVisible]);

  // 当导入模式变化时，重新加载数据表列表并重置状态
  useEffect(() => {
    if (importMode === 'create') {
      setSelectedSheetId('');
      setFields([]);
      setMatchFields([]);
      setFieldMappings([]);
      setShowFieldMapping(false);
    } else {
      // 模式2-5需要数据表，重新加载数据表列表
      loadAllSheets();
      // 如果切换到需要数据表的模式，且没有选择数据表，默认使用当前激活的数据表
      if (activeSheet && !selectedSheetId) {
        handleSheetChange(activeSheet.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importMode]);

  // 当选择数据表或字段变化时，自动生成字段映射（模式2-5）
  useEffect(() => {
    if (importMode !== 'create' && selectedSheetId && fields.length > 0 && parsedData) {
      const sourceFields = parsedData.headers || [];
      const mappings = generateFieldMappings(fields, sourceFields, enableFuzzyMatch);
      setFieldMappings(mappings);
      setShowFieldMapping(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSheetId, fields, parsedData, importMode, enableFuzzyMatch]);

  // 当字段映射变化时，清除不在可用列表中的匹配字段
  useEffect(() => {
    if (matchFields.length > 0 && matchFieldOptions.length > 0) {
      const availableFieldValues = matchFieldOptions.map(opt => opt.value);
      const invalidFields = matchFields.filter(field => !availableFieldValues.includes(field));
      if (invalidFields.length > 0) {
        console.warn('清除无效的匹配字段:', invalidFields);
        setMatchFields(matchFields.filter(field => availableFieldValues.includes(field)));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldMappings, matchFieldOptions]);

  // 处理文件选择
  const handleFileSelect = useCallback(async (file: File) => {
    setLoading(true);
    setFile(file);
    setImportProgress(0);
    setImportStatus('');
    
    try {
      console.log('[文件解析] 开始解析文件:', file.name, '类型:', file.type, '大小:', file.size);
      
      // 解析Excel文件
      const data = await parseExcelFile(file);
      setParsedData(data);
      
      console.log('[文件解析] 解析结果 - 表头:', data.headers);
      console.log('[文件解析] 解析结果 - 数据行数:', data.totalRows);
      console.log('[文件解析] 解析结果 - 列数:', data.totalCols);
      
      // 预览前3行
      const preview = data.rows.slice(0, 3);
      setPreviewRows(preview);
      
      // 智能识别字段类型
      const predictions = predictFieldTypes(data.headers, preview);
      setFieldPredictions(predictions);
      
      // 判断第一行是否为表头
      const isHeader = isFirstRowHeader([data.rows[0], data.rows[1]].filter(Boolean));
      setFirstRowIsHeader(isHeader);
      
      // 设置新表名（基于文件名）
      const fileName = file.name.replace(/\.(xlsx|xls|csv)$/i, '');
      setNewSheetName(fileName);
      
      // 初始化字段映射
      const fieldMap: Record<string, string> = {};
      predictions.forEach(pred => {
        fieldMap[pred.name] = pred.suggestedName || pred.name;
      });
      setSelectedFields(fieldMap);
      
      console.log('✅ 文件解析成功');
      // 移除成功提示弹窗，只在控制台输出
    } catch (error: any) {
      console.error(`❌ 文件解析失败:`, error);
      alert(`文件解析失败: ${error.message || '未知错误'}\n\n请检查：\n1. 文件格式是否正确\n2. 文件是否包含数据\n3. 文件编码是否为 UTF-8（CSV文件）`);
    } finally {
      setLoading(false);
    }
  }, []);


  // 统一的文件选择处理（Excel文件或在线表格）
  const handleSelectDingtalkFileOrSheet = useCallback(async () => {
    // 打开文件浏览器
    setShowFileBrowser(true);
  }, []);

  // 处理从文件浏览器选择的文件
  const handleFileBrowserSelect = useCallback(async (selectedFile: DingTalkFile) => {
    setSelectingDingtalkFile(true);
    setLoading(true);
    setImportProgress(0);
    setImportStatus('正在下载文件...');
    
    try {
      console.log('[文件选择] 从文件浏览器选择文件:', selectedFile);
      setDingtalkFile(selectedFile);
      
      // 下载文件并转换为File对象
      const fileObj = await downloadDingTalkFile(selectedFile);
      setFile(fileObj);
      
      // 解析文件
      await handleFileSelect(fileObj);
      
      setSelectingDingtalkFile(false);
      setImportStatus('');
    } catch (error: any) {
      console.error('[文件选择] 处理文件失败:', error);
      alert(`处理文件失败：${error.message}\n\n请检查：\n1. 网络连接是否正常\n2. 文件是否可访问\n3. 文件格式是否正确`);
      setSelectingDingtalkFile(false);
      setLoading(false);
      setImportStatus('');
    }
  }, [handleFileSelect]);

  // 带超时的 script.run 包装函数
  // 用于大数据量导入，设置较长的超时时间（10分钟 = 600秒）
  // timeout 参数单位为秒（函数内部会转换为毫秒传给钉钉SDK）
  const scriptRunWithTimeout = async (scriptName: string, timeout: number = 600, ...args: any[]): Promise<any> => {
    // 在执行前检查是否已取消
    if (isImportCancelledRef.current) {
      console.log(`[取消检查] 已取消导入，跳过 ${scriptName} 调用`);
      throw new Error('导入已取消');
    }
    
    // 记录调用开始时间和参数
    const startTime = Date.now();
    // 钉钉 SDK 的 runWithTimeout 使用毫秒作为单位，所以需要将秒转换为毫秒
    const timeoutMs = timeout * 1000;
    console.log(`[API调用] 开始调用 ${scriptName}，超时设置: ${timeout}秒 (${timeoutMs}毫秒)，参数数量: ${args.length}`);
    
    try {
      // 使用 runWithTimeout 方法，第二个参数为超时时间（单位为毫秒）
      if (typeof (window as any).Dingdocs?.script?.runWithTimeout === 'function') {
        console.log(`[API调用] 使用 runWithTimeout 方法，超时时间: ${timeout}秒 (${timeoutMs}毫秒)`);
        // 包装Promise，确保所有错误都被捕获，包括内部未捕获的错误
        // 注意：钉钉 SDK 的 runWithTimeout 第二个参数是毫秒，不是秒
        const promise = (window as any).Dingdocs.script.runWithTimeout(scriptName, timeoutMs, ...args);
        
        // 添加超时监控（用于诊断问题）
        const timeoutMonitor = setTimeout(() => {
          const elapsed = (Date.now() - startTime) / 1000;
          console.warn(`[超时监控] ${scriptName} 已执行 ${elapsed.toFixed(2)}秒，设置的超时时间为 ${timeout}秒 (${timeoutMs}毫秒)`);
        }, Math.min(timeoutMs * 0.8, 5000)); // 在80%超时时间或5秒时提醒（取较小值）
        
        // 使用 Promise.race 来监控取消标志，如果检测到取消，立即中断
        // 注意：这只能中断前端的等待，无法真正停止后端脚本的执行
        // 但可以阻止后续的 API 调用，从而减少不必要的操作
        let cancelCheckInterval: NodeJS.Timeout | null = null;
        const cancelPromise = new Promise((_, reject) => {
          // 立即检查一次取消标志
          if (isImportCancelledRef.current) {
            clearTimeout(timeoutMonitor);
            console.log(`[取消检查] 在开始执行前检测到取消标志，立即中断 ${scriptName}`);
            reject(new Error('导入已取消'));
            return;
          }
          
          // 定期检查取消标志（更频繁的检查，每10ms一次，提高响应速度）
          cancelCheckInterval = setInterval(() => {
            if (isImportCancelledRef.current) {
              if (cancelCheckInterval) clearInterval(cancelCheckInterval);
              clearTimeout(timeoutMonitor);
              console.log(`[取消检查] 检测到取消标志，立即中断 ${scriptName} 的执行`);
              reject(new Error('导入已取消'));
            }
          }, 10); // 每10ms检查一次取消标志，提高响应速度
        });
        
        // 添加catch处理，确保错误被正确捕获
        let promiseCompleted = false;
        const result = await Promise.race([
          promise
            .then((res: any) => {
              promiseCompleted = true;
              if (cancelCheckInterval) clearInterval(cancelCheckInterval);
              clearTimeout(timeoutMonitor);
              const elapsed = (Date.now() - startTime) / 1000;
              console.log(`[API调用] ${scriptName} 执行成功，耗时: ${elapsed.toFixed(2)}秒`);
              return res;
            })
            .catch((error: any) => {
              promiseCompleted = true;
              if (cancelCheckInterval) clearInterval(cancelCheckInterval);
              clearTimeout(timeoutMonitor);
              const elapsed = (Date.now() - startTime) / 1000;
              const errorMessage = error?.message || String(error || '未知错误');
              const errorString = String(error || '');
              console.error(`[API调用] ${scriptName} 执行失败，耗时: ${elapsed.toFixed(2)}秒`);
              console.error(`[API调用] 错误消息: "${errorMessage}"`);
              console.error(`[API调用] 错误字符串: "${errorString}"`);
              console.error(`[API调用] 完整错误对象:`, error);
              // 确保错误被正确包装和重新抛出
              const wrappedError = error instanceof Error ? error : new Error(String(error || '未知错误'));
              throw wrappedError;
            }),
          cancelPromise.catch((error: any) => {
            // 如果取消检查先完成，清理资源
            if (!promiseCompleted && cancelCheckInterval) {
              clearInterval(cancelCheckInterval);
            }
            clearTimeout(timeoutMonitor);
            throw error;
          })
        ]);
        
        // 执行后再次检查是否已取消
        if (isImportCancelledRef.current) {
          console.log(`[取消检查] 执行 ${scriptName} 后检测到取消，停止后续操作`);
          throw new Error('导入已取消');
        }
        return result;
      }
      // 如果没有 runWithTimeout，使用普通的 run 方法
      // 注意：这种情况下超时由钉钉平台控制，可能无法自定义
      console.warn(`[API调用] runWithTimeout 方法不可用，使用普通 run 方法（超时时间由平台控制）`);
      // 包装Promise，确保所有错误都被捕获
      const promise = Dingdocs.script.run(scriptName, ...args);
      
      // 添加超时监控（用于诊断问题）
      const timeoutMonitor = setTimeout(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        console.warn(`[超时监控] ${scriptName} 已执行 ${elapsed.toFixed(2)}秒（使用普通run方法，超时由平台控制）`);
      }, 5000); // 5秒提醒
      
      // 使用 Promise.race 来监控取消标志，如果检测到取消，立即中断
      // 注意：这只能中断前端的等待，无法真正停止后端脚本的执行
      // 但可以阻止后续的 API 调用，从而减少不必要的操作
      let cancelCheckInterval: NodeJS.Timeout | null = null;
      const cancelPromise = new Promise((_, reject) => {
        // 立即检查一次取消标志
        if (isImportCancelledRef.current) {
          clearTimeout(timeoutMonitor);
          console.log(`[取消检查] 在开始执行前检测到取消标志，立即中断 ${scriptName}`);
          reject(new Error('导入已取消'));
          return;
        }
        
        // 定期检查取消标志（更频繁的检查，每50ms一次）
        cancelCheckInterval = setInterval(() => {
          if (isImportCancelledRef.current) {
            if (cancelCheckInterval) clearInterval(cancelCheckInterval);
            clearTimeout(timeoutMonitor);
            console.log(`[取消检查] 检测到取消标志，中断 ${scriptName} 的执行`);
            reject(new Error('导入已取消'));
          }
        }, 50); // 每50ms检查一次取消标志，提高响应速度
      });
      
      // 添加catch处理，确保错误被正确捕获
      let promiseCompleted = false;
      const result = await Promise.race([
        promise
          .then((res: any) => {
            promiseCompleted = true;
            if (cancelCheckInterval) clearInterval(cancelCheckInterval);
            clearTimeout(timeoutMonitor);
            const elapsed = (Date.now() - startTime) / 1000;
            console.log(`[API调用] ${scriptName} 执行成功，耗时: ${elapsed.toFixed(2)}秒`);
            return res;
          })
          .catch((error: any) => {
            promiseCompleted = true;
            if (cancelCheckInterval) clearInterval(cancelCheckInterval);
            clearTimeout(timeoutMonitor);
            const elapsed = (Date.now() - startTime) / 1000;
            const errorMessage = error?.message || String(error || '未知错误');
            const errorString = String(error || '');
            console.error(`[API调用] ${scriptName} 执行失败，耗时: ${elapsed.toFixed(2)}秒`);
            console.error(`[API调用] 错误消息: "${errorMessage}"`);
            console.error(`[API调用] 错误字符串: "${errorString}"`);
            console.error(`[API调用] 完整错误对象:`, error);
            // 确保错误被正确包装和重新抛出
            const wrappedError = error instanceof Error ? error : new Error(String(error || '未知错误'));
            throw wrappedError;
          }),
        cancelPromise.catch((error: any) => {
          // 如果取消检查先完成，清理资源
          if (!promiseCompleted && cancelCheckInterval) {
            clearInterval(cancelCheckInterval);
          }
          clearTimeout(timeoutMonitor);
          throw error;
        })
      ]);
      
      // 执行后再次检查是否已取消
      if (isImportCancelledRef.current) {
        console.log(`[取消检查] 执行 ${scriptName} 后检测到取消，停止后续操作`);
        throw new Error('导入已取消');
      }
      return result;
    } catch (error: any) {
      // 检测超时错误 - 更精确的判断，避免误判
      const errorMessage = error?.message || String(error || '未知错误');
      const errorMessageLower = errorMessage.toLowerCase();
      
      // 更严格的超时错误判断：必须是明确的超时错误，而不是包含timeout字符串的其他错误
      // 检查错误消息是否明确表示超时（排除其他可能包含timeout字符串的情况）
      const isTimeoutError = (
        errorMessageLower === 'timeout' ||
        errorMessageLower === '超时' ||
        errorMessageLower.includes('timeout error') ||
        errorMessageLower.includes('超时错误') ||
        errorMessageLower.includes('execution timeout') ||
        errorMessageLower.includes('请求超时') ||
        errorMessageLower.includes('操作超时') ||
        (errorMessageLower.includes('timeout') && (
          errorMessageLower.includes('exceeded') ||
          errorMessageLower.includes('expired') ||
          errorMessageLower.includes('timed out')
        ))
      );
      
      // 计算实际执行时间
      const elapsed = (Date.now() - startTime) / 1000;
      
      // 记录详细的错误信息
      console.error(`[API错误] ${scriptName} 执行失败:`);
      console.error(`  - 错误消息: "${errorMessage}"`);
      console.error(`  - 错误类型: ${error?.name || 'Unknown'}`);
      console.error(`  - 实际执行时间: ${elapsed.toFixed(2)}秒`);
      console.error(`  - 设置的超时时间: ${timeout}秒`);
      console.error(`  - 是否在导入过程中: ${isImportingRef.current}`);
      console.error(`  - 完整错误对象:`, error);
      
      // 如果实际执行时间远小于设置的超时时间，可能是其他错误被误判为超时
      // 钉钉SDK内部可能有默认的超时限制（如1秒），即使我们设置了更长的超时时间
      const isFalseTimeout = isTimeoutError && elapsed < timeout * 0.1;
      
      if (isFalseTimeout) {
        console.warn(`[超时误判警告] ${scriptName} 错误消息包含"timeout"，但实际执行时间(${elapsed.toFixed(2)}秒)远小于设置的超时时间(${timeout}秒)`);
        console.warn(`[超时误判警告] 这可能是其他错误（如网络错误、权限错误、Mixed Content等）被误判为超时错误`);
        console.warn(`[超时误判警告] 建议检查：1. 网络连接 2. 权限配置 3. API调用参数 4. Mixed Content问题（HTTPS页面请求HTTP）`);
        console.warn(`[超时误判警告] 钉钉SDK内部可能有默认的超时限制，即使设置了更长的超时时间也可能无效`);
        // 如果是误判，不将其视为真正的超时错误，不设置取消标志
      }
      
      // 记录错误详情，帮助调试
      if (errorMessageLower.includes('timeout') || errorMessageLower.includes('超时')) {
        console.log(`[错误分析] ${scriptName} 错误消息: "${errorMessage}"`);
        console.log(`[错误分析] 是否判定为超时: ${isTimeoutError}`);
        console.log(`[错误分析] 是否误判: ${isFalseTimeout}`);
        console.log(`[错误分析] 实际执行时间: ${elapsed.toFixed(2)}秒`);
        console.log(`[错误分析] 设置的超时时间: ${timeout}秒`);
        console.log(`[错误分析] 是否在导入过程中: ${isImportingRef.current}`);
      }
      
      // 只有在导入过程中（isImportingRef.current === true）的真正的超时才设置取消标志
      // 初始化阶段的超时不应该设置取消标志，避免影响后续初始化操作
      // 误判的超时（实际执行时间远小于设置的超时时间）也不应该设置取消标志
      if (isTimeoutError && isImportingRef.current && !isFalseTimeout) {
        console.error(`[超时错误] ${scriptName} 执行超时（导入过程中），设置取消标志以停止后续操作`);
        isImportCancelledRef.current = true;
        setLoading(false);
        setImportStatus('导入超时，已停止');
        isImportingRef.current = false; // 重置导入标志
      } else if (isTimeoutError && isImportingRef.current && isFalseTimeout) {
        // 误判的超时，不设置取消标志，但记录警告
        console.warn(`[超时误判] ${scriptName} 虽然错误消息是"timeout"，但实际执行时间很短，可能是其他错误，不设置取消标志`);
        console.warn(`[超时误判] 建议检查网络连接、权限配置、Mixed Content等问题`);
      } else if (isTimeoutError && !isImportingRef.current) {
        // 初始化阶段的超时，只记录警告，不设置取消标志，不影响后续操作
        console.warn(`[超时警告] ${scriptName} 执行超时（初始化阶段），但不影响后续操作`);
        console.warn(`[超时警告] 错误消息: "${errorMessage}"`);
        // 不设置取消标志，允许继续初始化
      } else if (errorMessageLower.includes('timeout') || errorMessageLower.includes('超时')) {
        // 如果包含timeout但不是真正的超时错误，只记录警告
        console.warn(`[错误警告] ${scriptName} 错误消息包含"timeout"但可能不是真正的超时错误: ${errorMessage}`);
        if (!isImportingRef.current) {
          console.warn(`[错误警告] 当前处于初始化阶段，不会设置取消标志`);
        }
      }
      
      // 重新抛出错误，让上层处理
      // 确保错误被正确包装，避免未捕获的Promise错误
      const wrappedError = error instanceof Error ? error : new Error(String(error));
      throw wrappedError;
    }
  };

  // 停止导入
  const handleStopImport = useCallback(() => {
    console.log('[停止导入] 用户点击了停止导入按钮');
    isImportCancelledRef.current = true;
    setLoading(false);
    setImportStatus('导入已取消');
    isImportingRef.current = false; // 重置导入标志，确保后续检查能正确识别
    console.log('[停止导入] 取消标志已设置，isImportCancelledRef.current =', isImportCancelledRef.current);
    console.log('[停止导入] 导入标志已重置，isImportingRef.current =', isImportingRef.current);
  }, []);

  // 继续导入（在删除重复数据后）
  const handleContinueImport = async () => {
    if (!parsedData) {
      return;
    }
    
    try {
      setLoading(true);
      
      // 准备数据
      const dataRows = firstRowIsHeader ? parsedData.rows : parsedData.rows;
      const records = dataRows.map((row) => {
        const fields: Record<string, any> = {};
        
        if (fieldMappings.length > 0) {
          const isMatchField = (targetField: string) => {
            return (importMode === 'updateBlank' || importMode === 'updateExisting' || importMode === 'updateBoth') 
              && matchFields.includes(targetField);
          };
          
          fieldMappings.forEach(mapping => {
            if (mapping.sourceField && mapping.sourceField !== '') {
              const sourceValue = row[mapping.sourceField];
              const targetField = mapping.targetField;
              const isMatch = isMatchField(targetField);
              
              if (sourceValue !== undefined && sourceValue !== null && sourceValue !== '') {
                fields[targetField] = sourceValue;
              } else if (isMatch) {
                fields[targetField] = null;
              }
            }
          });
        } else {
          Object.keys(row).forEach(key => {
            fields[key] = row[key];
          });
        }
        
        return { fields };
      });
      
      // 清空重复数据状态，继续执行导入（合并选项会在后端函数内部处理）
      setDuplicateData(null);
      
      // 继续执行原有的导入逻辑，跳过重复数据查询（因为已经处理过了）
      await handleImportWithRecords(records, true);
    } catch (err: any) {
      console.error('继续导入失败:', err);
      alert(`继续导入失败: ${err.message}`);
      setLoading(false);
    }
  };
  
  // 执行导入（主函数）
  const handleImport = async () => {
    if (!parsedData) {
      alert('请先选择文件或在线表格');
      return;
    }

    // 开始捕获日志
    const restoreConsole = startLogCapture();

    // 重置取消标志和导入标志
    isImportCancelledRef.current = false;
    isImportingRef.current = true;
    setLoading(true);
    setImportProgress(0);
    setImportStatus('准备导入...');
    // 重置查询进度
    setQueryProgress({
      isQuerying: false,
      currentQuery: 0,
      totalRecords: 0,
      totalRecordsEstimate: 0,
      matchedKeys: 0,
      targetKeys: 0,
      status: '',
      sheetName: undefined
    });

    try {
      // 准备数据
      const dataRows = firstRowIsHeader ? parsedData.rows : parsedData.rows;
      let records = dataRows.map((row) => {
        const fields: Record<string, any> = {};
        
        if (fieldMappings.length > 0) {
          const isMatchField = (targetField: string) => {
            return (importMode === 'updateBlank' || importMode === 'updateExisting' || importMode === 'updateBoth') 
              && matchFields.includes(targetField);
          };
          
          fieldMappings.forEach(mapping => {
            if (mapping.sourceField && mapping.sourceField !== '') {
              const sourceValue = row[mapping.sourceField];
              const targetField = mapping.targetField;
              const isMatch = isMatchField(targetField);
              
              if (sourceValue !== undefined && sourceValue !== null && sourceValue !== '') {
                fields[targetField] = sourceValue;
              } else if (isMatch) {
                fields[targetField] = null;
              }
            }
          });
        } else {
          Object.keys(row).forEach(key => {
            fields[key] = row[key];
          });
        }
        
        return { fields };
      });

      // 如果启用了重复数据合并且是模式3-5，先查询数据表的重复数据
      if (!enableAggregation && enableDuplicateMerge && 
          (importMode === 'updateBlank' || importMode === 'updateExisting' || importMode === 'updateBoth')) {
        if (!selectedSheetId || matchFields.length === 0) {
          alert('请选择数据表和匹配字段（至少1个，最多3个）');
          setLoading(false);
          return;
        }
        
        // 关键修复：只在第一次导入之前查询重复数据，如果从断点继续（startBatch > 0），跳过查询
        const getStartBatch = (totalBatches: number) => {
          const checkpointStr = localStorage.getItem('import_checkpoint');
          if (checkpointStr) {
            try {
              const checkpoint = JSON.parse(checkpointStr);
              if (checkpoint.batchIndex && checkpoint.batchIndex > 0) {
                return Math.max(0, checkpoint.batchIndex - 1); // 从断点继续
              }
            } catch (e) {
              console.warn('解析断点信息失败:', e);
            }
          }
          return 0; // 第一次导入
        };
        
        // 先计算 startBatch，判断是否从断点继续
        // 注意：这里使用临时 chunkSize 计算，实际的 chunkSize 在后面定义
        const tempChunkSize = 2000;
        const tempTotalBatches = Math.ceil(records.length / tempChunkSize);
        const startBatch = getStartBatch(tempTotalBatches);
        const isResumingFromCheckpoint = startBatch > 0;
        
        // 只在第一次导入之前查询重复数据，如果从断点继续则跳过
        if (!isResumingFromCheckpoint) {
          try {
            // 查询数据表中的重复数据
            const sheetName = allSheets.find(s => s.id === selectedSheetId)?.name || '未知数据表';
            setImportStatus(`正在查询数据表"${sheetName}"中的重复数据...`);
            setQueryProgress({
              isQuerying: true,
              currentQuery: 0,
              totalRecords: 0,
              totalRecordsEstimate: 0,
              matchedKeys: 0,
              targetKeys: 0,
              status: `开始查询数据表"${sheetName}"...`,
              sheetName: sheetName
            });
            
            const sheetDuplicateResult = await scriptRunWithTimeout('querySheetDuplicateRecords', 3600,
              selectedSheetId,
              matchFields,
              duplicateMergeStrategy,
              (duplicateMergeStrategy === 'largestField' || duplicateMergeStrategy === 'smallestField') ? duplicateMergeField : undefined,
              selectedViewId || undefined
            );
            
            setQueryProgress({
              isQuerying: false,
              currentQuery: sheetDuplicateResult.totalQueries,
              totalRecords: sheetDuplicateResult.totalRecords,
              totalRecordsEstimate: sheetDuplicateResult.totalRecords,
              matchedKeys: sheetDuplicateResult.uniqueMatchKeys,
              targetKeys: sheetDuplicateResult.uniqueMatchKeys,
              status: `查询完成，数据表"${sheetDuplicateResult.sheetName}"共 ${sheetDuplicateResult.totalRecords} 条记录，发现 ${sheetDuplicateResult.duplicateGroups.length} 组重复数据`,
              sheetName: sheetDuplicateResult.sheetName
            });
            
            console.log('数据表重复数据查询结果:', sheetDuplicateResult);
            
            // 验证返回数据的完整性
            if (!sheetDuplicateResult || typeof sheetDuplicateResult !== 'object') {
              console.error('查询结果格式错误:', sheetDuplicateResult);
              alert('查询重复数据失败：返回数据格式错误');
              setLoading(false);
              return;
            }
            
            // 确保 duplicateGroups 存在且是数组
            if (!sheetDuplicateResult.duplicateGroups || !Array.isArray(sheetDuplicateResult.duplicateGroups)) {
              console.error('查询结果中 duplicateGroups 格式错误:', sheetDuplicateResult.duplicateGroups);
              alert('查询重复数据失败：返回数据格式错误（duplicateGroups 不是数组）');
              setLoading(false);
              return;
            }
            
            // 如果有重复数据，显示UI让用户选择删除
            if (sheetDuplicateResult.duplicateGroups.length > 0) {
              // 存储重复数据详情，显示UI
              console.log('[状态更新] 准备设置 duplicateData:', {
                duplicateGroupsLength: sheetDuplicateResult.duplicateGroups.length,
                duplicateCount: sheetDuplicateResult.duplicateCount,
                sheetName: sheetDuplicateResult.sheetName
              });
              console.log('[状态更新] 设置 duplicateData 前的状态:', {
                duplicateData: duplicateData ? '已存在' : 'null',
                loading,
                queryProgressIsQuerying: queryProgress.isQuerying
              });
              
              setDuplicateData(sheetDuplicateResult);
              setImportStatus(`发现 ${sheetDuplicateResult.duplicateGroups.length} 组重复数据，共 ${sheetDuplicateResult.duplicateCount} 条重复记录，请选择要删除的记录`);
              setLoading(false);
              // 清除查询进度状态，确保查询进度UI不再显示
              setQueryProgress(prev => ({
                ...prev,
                isQuerying: false
              }));
              
              console.log('[状态更新] duplicateData 已设置，loading 已设为 false，queryProgress.isQuerying 已设为 false');
              console.log('[状态更新] 设置 duplicateData 后的状态（通过 setTimeout 检查）:', {
                timestamp: new Date().toISOString()
              });
              
              // 使用 setTimeout 验证状态是否已更新
              setTimeout(() => {
                console.log('[状态更新验证] 500ms 后的状态:', {
                  duplicateData: duplicateData ? {
                    hasData: true,
                    duplicateGroupsLength: duplicateData.duplicateGroups?.length || 0
                  } : 'null',
                  loading,
                  queryProgressIsQuerying: queryProgress.isQuerying
                });
              }, 500);
              
              // 等待用户操作，不继续执行导入
              console.log('[流程控制] 返回，等待用户操作（删除重复数据或跳过）');
              return;
            } else {
              // 没有重复数据，自动继续处理导入数据
              console.log('[自动继续] 未发现重复数据，自动继续导入...');
              setDuplicateData(null);
              setImportStatus('未发现重复数据，正在继续导入...');
              // 清除查询进度状态
              setQueryProgress(prev => ({
                ...prev,
                isQuerying: false
              }));
            }
          } catch (err: any) {
            console.error('查询重复数据失败:', err);
            // 如果是取消错误，直接返回，不继续执行导入
            if (err instanceof Error && err.message === '导入已取消') {
              setImportStatus('导入已取消');
              setLoading(false);
              isImportCancelledRef.current = false;
              isImportingRef.current = false;
              return;
            }
            alert(`查询重复数据失败: ${err.message}\n\n将继续执行导入，但不会进行重复数据处理`);
          }
        } else {
          // 从断点继续，跳过重复数据查询
          console.log(`[断点继续] 从第 ${startBatch + 1} 批继续导入，跳过重复数据查询`);
          setImportStatus(`从第 ${startBatch + 1} 批继续导入...`);
        }
      }
      
      // 继续执行导入（合并选项会在后端函数内部处理）
      await handleImportWithRecords(records);
    } catch (err: any) {
      console.error('导入失败:', err);
      if (err instanceof Error && err.message === '导入已取消') {
        setImportStatus('导入已取消');
        isImportCancelledRef.current = false;
        isImportingRef.current = false;
        // 注意：这里不 return，让 finally 块执行以确保 loading 被重置
      } else {
        alert(`导入失败: ${err.message}`);
      }
      // 确保 loading 被重置（在 finally 块中也会重置，但这里也重置以确保）
      setLoading(false);
      isImportingRef.current = false;
    } finally {
      // 确保 loading 被重置，无论成功、失败还是取消
      setLoading(false);
      isImportCancelledRef.current = false;
      isImportingRef.current = false;
      restoreConsole();
    }
  };

  // 执行导入（内部函数，接受已处理的records）
  // 使用函数声明以便在定义前使用（函数提升）
  async function handleImportWithRecords(
    processedRecords: Array<{ fields: Record<string, any> }>,
    skipDuplicateCheck: boolean = false  // 新增参数：跳过重复数据查询（用于删除重复数据后继续导入）
  ) {
    if (!parsedData) {
      alert('请先选择文件或在线表格');
      return;
    }

    // 开始捕获日志
    const restoreConsole = startLogCapture();

    // 重置取消标志和导入标志
    isImportCancelledRef.current = false;
    isImportingRef.current = true; // 标记开始导入
    setLoading(true);
    setImportProgress(0);
    setImportStatus('准备导入...');
    // 重置查询进度
    setQueryProgress({
      isQuerying: false,
      currentQuery: 0,
      totalRecords: 0,
      totalRecordsEstimate: 0,
      matchedKeys: 0,
      targetKeys: 0,
      status: '',
      sheetName: undefined
    });

    try {
      let result: any;
      // 优化的授权刷新策略
      // 钉钉 access_token 和 jsapi_ticket 通常有效期为2小时
      // 为了安全，我们设置更短的刷新间隔
      let lastPermissionRefreshTime = 0;
      const PERMISSION_REFRESH_INTERVAL = 5 * 60 * 1000; // 5分钟刷新一次
      const PERMISSION_REFRESH_BATCH_INTERVAL = 10; // 每10个分段刷新一次
      const getStartBatch = (totalBatches: number) => {
        const val = Math.max(0, Math.floor(Number(startChunk || 1)) - 1);
        if (val >= totalBatches) {
          throw new Error(`起始分段(${startChunk}) 超过总分段(${totalBatches})，请调整后重试`);
        }
        return val;
      };
      
      const refreshPermissionPerBatch = async (batchIndex: number, totalBatches: number) => {
        // 在执行前检查是否已取消
        if (isImportCancelledRef.current) {
          console.log(`[取消检查] 已取消导入，跳过授权刷新`);
          throw new Error('导入已取消');
        }
        
        const now = Date.now();
        const shouldRefresh = 
          batchIndex === 0 || // 第一个分段必须刷新
          (now - lastPermissionRefreshTime) >= PERMISSION_REFRESH_INTERVAL || // 超过5分钟
          (batchIndex % PERMISSION_REFRESH_BATCH_INTERVAL === 0); // 每10个分段刷新一次
        
        if (shouldRefresh) {
          try {
            console.log(`[授权刷新] 第${batchIndex + 1}/${totalBatches}批，刷新授权...`);
            await configurePermission();
            lastPermissionRefreshTime = now;
            console.log(`[授权刷新] 授权刷新成功`);
            
            // 执行后再次检查是否已取消
            if (isImportCancelledRef.current) {
              console.log(`[取消检查] 授权刷新后检测到取消，停止后续操作`);
              throw new Error('导入已取消');
            }
          } catch (err) {
            // 如果是取消错误，直接抛出
            if (err instanceof Error && err.message === '导入已取消') {
              throw err;
            }
            console.error('刷新权限失败:', err);
            throw err;
          }
        } else {
          console.log(`[授权刷新] 第${batchIndex + 1}/${totalBatches}批，跳过授权刷新（距离上次刷新 ${Math.floor((now - lastPermissionRefreshTime) / 1000)}秒）`);
        }
      };

      // 准备数据 - 使用字段映射
      const dataRows = firstRowIsHeader ? parsedData.rows : parsedData.rows;
      
      // 统一分块函数，避免单次提交过大导致超时
      // 优化策略：
      // - 前端分块：1000行（与稳定性检查间隔一致，减少script.run调用次数）
      // - 后端分批：script内部会再次分批为500条（符合API限制）
      // - 这样可以减少授权刷新和网络请求次数，提升性能，同时与稳定性检查机制配合
      const chunkSize = 2000; // 前端分块大小（后端会再次分批为500条，每批处理完成后执行深度验证）
      const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
      const chunkArray = <T,>(arr: T[], size: number): T[][] => {
        const res: T[][] = [];
        for (let i = 0; i < arr.length; i += size) {
          res.push(arr.slice(i, i + size));
        }
        return res;
      };

      let records = dataRows.map((row, rowIndex) => {
        const fields: Record<string, any> = {};
        
        // 如果有字段映射，使用字段映射；否则使用旧的逻辑
        if (fieldMappings.length > 0) {
          // 统计实际使用的字段（用于调试）
          if (rowIndex === 0) {
            const usedMappings = fieldMappings.filter(m => m.sourceField && m.sourceField !== '');
            console.log(`[数据准备] 第1行数据准备：使用 ${usedMappings.length} 个字段映射（共 ${fieldMappings.length} 个映射）`);
            if (usedMappings.length < fieldMappings.length) {
              const unusedMappings = fieldMappings.filter(m => !m.sourceField || m.sourceField === '');
              console.log(`[数据准备] 未使用的字段映射数: ${unusedMappings.length}`);
              if (unusedMappings.length <= 20) {
                console.log(`[数据准备] 未使用的字段列表:`, unusedMappings.map(m => m.targetField));
              }
            }
          }
          
          // 判断是否为匹配字段
          const isMatchField = (targetField: string) => {
            return (importMode === 'updateBlank' || importMode === 'updateExisting' || importMode === 'updateBoth') 
              && matchFields.includes(targetField);
          };
          
          // 统一的字段映射逻辑（避免重复设置）
          fieldMappings.forEach(mapping => {
            if (mapping.sourceField && mapping.sourceField !== '') {
              const sourceValue = row[mapping.sourceField];
              const targetField = mapping.targetField;
              const isMatch = isMatchField(targetField);
              
              if (isMatch) {
                // 匹配字段：允许空值，必须设置（即使为空也要设置为空字符串或null）
                if (sourceValue !== undefined && sourceValue !== null) {
                  fields[targetField] = sourceValue;
                } else {
                  // 显式设置为空字符串，确保匹配键生成时能正确处理
                  fields[targetField] = '';
                }
                
                if (rowIndex < 3) {
                  console.log(`第${rowIndex + 1}行：匹配字段"${targetField}"从导入文件字段"${mapping.sourceField}"获取值:`, sourceValue || '(空)');
                }
              } else {
                // 非匹配字段：跳过空值
                if (sourceValue !== undefined && sourceValue !== null && sourceValue !== '') {
                  // 避免覆盖已设置的字段（虽然理论上不应该发生）
                  if (fields[targetField] === undefined) {
                    fields[targetField] = sourceValue;
                  }
                }
              }
            }
          });
        } else {
          // 兼容旧逻辑
          Object.keys(row).forEach(key => {
            const targetField = selectedFields[key] || key;
            fields[targetField] = row[key];
          });
        }
        
        return { fields };
      });

      // 验证汇总配置（如果启用）
      if (enableAggregation) {
        if (aggregationFields.length === 0) {
          alert('请选择至少1个分组字段进行汇总');
          setLoading(false);
          return;
        }
        
        if (aggregationFields.length > 3) {
          alert('分组字段最多只能选择3个');
          setLoading(false);
          return;
        }
      }

      // 准备字段配置
      let fieldConfigs: any[] = [];
      if (fieldMappings.length > 0) {
        // 使用字段映射生成字段配置
        fieldMappings.forEach(mapping => {
          if (mapping.isNewField && mapping.sourceField && mapping.sourceField !== '') {
            // 新增字段需要配置类型
            const pred = fieldPredictions.find(p => p.name === mapping.sourceField);
            if (pred) {
              fieldConfigs.push({
                name: mapping.targetField,
                type: pred.type,
                property: pred.type === 'singleSelect' ? {
                  choices: Array.from(new Set(dataRows.map(row => row[mapping.sourceField]).filter(Boolean))).map(val => ({
                    name: String(val),
                    color: '#1890ff'
                  }))
                } : undefined
              });
            }
          }
        });
      } else {
        // 兼容旧逻辑
        fieldConfigs = fieldPredictions.map(pred => ({
          name: selectedFields[pred.name] || pred.suggestedName || pred.name,
          type: pred.type,
          property: pred.type === 'singleSelect' ? {
            choices: Array.from(new Set(dataRows.map(row => row[pred.name]).filter(Boolean))).map(val => ({
              name: String(val),
              color: '#1890ff'
            }))
          } : undefined
        }));
      }

      setImportProgress(20);
      setImportStatus('正在导入数据...');

      switch (importMode) {
        case 'create':
          if (!newSheetName.trim()) {
            alert('请输入新表格名称');
            setLoading(false);
            return;
          }
          // 前端分块：首批创建表并导入，其余批次追加
          const createChunks = chunkArray(records, chunkSize);
          setTotalChunks(createChunks.length);
          setChunkProgress({ current: 0, total: createChunks.length, success: 0, failed: 0 });
          
          // 记录字段创建阶段开始
          performanceMetricsRef.current.stages['字段创建'].start = Date.now();
          
          let createdSheetId: string | undefined;
          let createdSheetName: string | undefined;
          let totalSuccess = 0;
          let totalError = 0;
          const allErrors: string[] = [];

          const startBatch = getStartBatch(createChunks.length);

          // 记录数据导入阶段开始
          performanceMetricsRef.current.stages['数据导入'].start = Date.now();
          if (performanceMetricsRef.current.stages['字段创建'].start > 0) {
            performanceMetricsRef.current.stages['字段创建'].end = Date.now();
            performanceMetricsRef.current.stages['字段创建'].duration = 
              performanceMetricsRef.current.stages['字段创建'].end - performanceMetricsRef.current.stages['字段创建'].start;
          }
          
          for (let idx = startBatch; idx < createChunks.length; idx++) {
            // 检查是否已取消导入
            if (isImportCancelledRef.current) {
              console.log(`导入已取消，停止在第 ${idx + 1} 批`);
              setImportStatus('导入已取消');
              break;
            }
            
            const chunk = createChunks[idx];
            const chunkStart = idx * chunkSize;
            setCurrentChunk(idx + 1);
            setChunkProgress(prev => ({ ...prev, current: idx + 1 }));
            setImportStatus(`正在导入数据... (${idx + 1}/${createChunks.length})`);
            setImportProgress(20 + Math.floor((idx / createChunks.length) * 60));
            
            try {
              // 在关键操作前再次检查取消标志
              if (isImportCancelledRef.current) {
                console.log(`导入已取消，停止在第 ${idx + 1} 批（API调用前）`);
                setImportStatus('导入已取消');
                break;
              }
              
              await refreshPermissionPerBatch(idx, createChunks.length);
              
              // 授权刷新后再次检查
              if (isImportCancelledRef.current) {
                console.log(`导入已取消，停止在第 ${idx + 1} 批（授权刷新后）`);
                setImportStatus('导入已取消');
                break;
              }
              
              if (idx === 0) {
                // 创建表并导入第一批数据，使用更长的超时时间（15分钟）
                // 因为创建表+导入数据可能需要较长时间
                const createRes = await scriptRunWithTimeout('createSheetAndImportData', 900, 
            newSheetName.trim(),
            fieldConfigs,
                  chunk,
                  ignoreErrors,
                  chunkStart
                );
                createdSheetId = createRes.sheetId;
                createdSheetName = createRes.sheetName;
                totalSuccess += createRes.successCount || 0;
                totalError += createRes.errorCount || 0;
                allErrors.push(...(createRes.errors || []));
              } else {
                if (!createdSheetId) {
                  throw new Error('未能获取新建表ID，无法继续追加数据');
                }
                const appendRes = await scriptRunWithTimeout('appendRecordsToSheet', 600,
                  createdSheetId,
                  chunk,
                  ignoreErrors,
                  undefined,
                  chunkStart
                );
                totalSuccess += appendRes.successCount || 0;
                totalError += appendRes.errorCount || 0;
                allErrors.push(...(appendRes.errors || []));
              }
              
              // 每批处理完成后执行深度验证（最多等待5分钟）
              // 最后一段数据插入完成，不需要进行深度验证
              if (createdSheetId && idx < createChunks.length - 1) {
                try {
                  setImportStatus(`正在验证表格状态... (${idx + 1}/${createChunks.length})`);
                  const verificationResult = await scriptRunWithTimeout('performExtendedStabilityCheck', 300,
                    createdSheetId,
                    totalSuccess,
                    '插入',
                    idx
                  );
                  
                  if (!verificationResult.success) {
                    // 关键修复：对于事件监听方式，如果验证失败，检查错误信息
                    // 如果是超时或没有收到事件，可能表格已稳定（不在计算状态），继续处理
                    const errorMsg = verificationResult.error || '';
                    const errorMsgLower = errorMsg.toLowerCase();
                    const isTimeoutOrNoEvent = (
                      (errorMsgLower.includes('等待') && errorMsgLower.includes('秒')) ||
                      errorMsgLower.includes('timeout') ||
                      errorMsgLower.includes('未收到') ||
                      errorMsgLower.includes('收到 0 个事件')
                    );
                    
                    if (isTimeoutOrNoEvent) {
                      // 超时或没有收到事件，可能表格已稳定，继续处理
                      console.log(`[深度验证] 第 ${idx + 1} 批验证超时或未收到事件（${errorMsg}），可能表格已稳定，继续处理`);
                    } else {
                      // 其他错误，保存断点并停止导入
                      const checkpoint = {
                        batchIndex: idx + 1, // 下一批从 idx + 1 开始
                        processedCount: totalSuccess,
                        sheetId: createdSheetId,
                        sheetName: createdSheetName,
                        timestamp: Date.now()
                      };
                      localStorage.setItem('import_checkpoint', JSON.stringify(checkpoint));
                      
                      const errorMsg2 = `第 ${idx + 1} 批处理完成后，表格验证失败：${errorMsg || '未知错误'}\n\n已保存断点，可从第 ${idx + 2} 批继续导入。`;
                      console.error(errorMsg2);
                      alert(errorMsg2);
                      
                      setLoading(false);
                      setImportStatus('导入已停止（验证失败）');
                      isImportingRef.current = false;
                      isImportCancelledRef.current = true;
                      break;
                    }
                  } else {
                    console.log(`[深度验证] 第 ${idx + 1} 批验证成功，继续下一批`);
                  }
                } catch (verifyErr: any) {
                  // 验证过程出错，但不影响主流程，记录警告
                  console.warn(`[深度验证] 第 ${idx + 1} 批验证过程出错: ${verifyErr.message}，继续处理...`);
                }
              }
              } catch (err: any) {
                // 如果是取消错误，直接退出循环
                if (err instanceof Error && err.message === '导入已取消') {
                  console.log(`[取消处理] 导入已取消，停止在第 ${idx + 1} 批`);
                  setImportStatus('导入已取消');
                  setLoading(false);
                  isImportingRef.current = false;
                  break;
                }
                
                // 检查是否在错误处理过程中被取消
                if (isImportCancelledRef.current) {
                  console.log(`[取消处理] 在错误处理过程中检测到取消，停止在第 ${idx + 1} 批`);
                  setImportStatus('导入已取消');
                  setLoading(false);
                  isImportingRef.current = false;
                  break;
                }
              
              // 检测超时错误，设置取消标志并退出循环
              // 使用更严格的超时错误判断，避免误判
              const errorMessage = err?.message || String(err || '未知错误');
              const errorMessageLower = errorMessage.toLowerCase();
              
              // 更严格的超时错误判断：必须是明确的超时错误
              const isTimeoutError = (
                errorMessageLower === 'timeout' ||
                errorMessageLower === '超时' ||
                errorMessageLower.includes('timeout error') ||
                errorMessageLower.includes('超时错误') ||
                errorMessageLower.includes('execution timeout') ||
                errorMessageLower.includes('请求超时') ||
                errorMessageLower.includes('操作超时') ||
                (errorMessageLower.includes('timeout') && (
                  errorMessageLower.includes('exceeded') ||
                  errorMessageLower.includes('expired') ||
                  errorMessageLower.includes('timed out')
                ))
              );
              
              // 记录错误详情，帮助调试
              if (errorMessageLower.includes('timeout') || errorMessageLower.includes('超时')) {
                console.log(`[错误分析] 第 ${idx + 1} 批错误消息: "${errorMessage}"`);
                console.log(`[错误分析] 是否判定为超时: ${isTimeoutError}`);
              }
              
              if (isTimeoutError) {
                console.error(`[超时错误] 第 ${idx + 1} 批导入超时，设置取消标志以停止后续操作`);
                isImportCancelledRef.current = true;
                setLoading(false);
                setImportStatus('导入超时，已停止');
                isImportingRef.current = false; // 重置导入标志
                break;
              } else if (errorMessageLower.includes('timeout') || errorMessageLower.includes('超时')) {
                // 如果包含timeout但不是真正的超时错误，只记录警告，不设置取消标志
                console.warn(`[错误警告] 第 ${idx + 1} 批错误消息包含"timeout"但可能不是真正的超时错误: ${errorMessage}`);
              }
              
              // 确保错误被正确包装，避免未捕获的Promise错误
              const wrappedErr = err instanceof Error ? err : new Error(String(err || '未知错误'));
              
              if (ignoreErrors) {
                totalError += chunk.length;
                allErrors.push(`第${idx + 1}批导入失败: ${wrappedErr.message}`);
                continue;
              }
              throw wrappedErr;
            }
            // 批次间隔前检查是否已取消
            if (isImportCancelledRef.current) {
              console.log(`[取消处理] 在批次间隔前检测到取消，停止在第 ${idx + 1} 批`);
              setImportStatus('导入已取消');
              setLoading(false);
              isImportingRef.current = false;
              break;
            }
            
            // 批次间隔（优化：减少延迟，提升性能）
            // 对于大数据量导入，1秒延迟可能过长，改为500ms
            await sleep(500);
            
            // 批次间隔后再次检查是否已取消
            if (isImportCancelledRef.current) {
              console.log(`[取消处理] 在批次间隔后检测到取消，停止在第 ${idx + 1} 批`);
              setImportStatus('导入已取消');
              setLoading(false);
              isImportingRef.current = false;
              break;
            }
          }

          result = {
            sheetId: createdSheetId,
            sheetName: createdSheetName,
            successCount: totalSuccess,
            errorCount: totalError,
            errors: allErrors
          };
          
          // 如果启用汇总，在导入后执行聚合合并
          if (enableAggregation && result.sheetId) {
            // 汇总前检查是否已取消
            if (isImportCancelledRef.current) {
              console.log('导入已取消，跳过数据汇总');
              setImportStatus('导入已取消');
            } else {
            setImportStatus('正在汇总数据...');
            setImportProgress(90);
            try {
                const aggregationResult = await scriptRunWithTimeout('aggregateSheetData', 600,
                result.sheetId,
                aggregationFields,
                sumFields,
                textSeparator || '；'
              );
              console.log(`数据汇总完成：原始 ${aggregationResult.originalCount} 条记录，合并后 ${aggregationResult.mergedCount} 条记录`);
              setImportStatus(`数据汇总完成：${aggregationResult.originalCount} → ${aggregationResult.mergedCount} 条`);
            } catch (error: any) {
                // 如果是取消错误，不显示错误提示
                if (error instanceof Error && error.message === '导入已取消') {
                  console.log('数据汇总已取消');
                  setImportStatus('导入已取消');
                } else {
              console.error('数据汇总失败:', error);
              alert(`数据汇总失败：${error.message}\n\n原始数据已导入成功`);
                }
              }
            }
          }
          break;

        case 'append':
          if (!selectedSheetId) {
            alert('请先选择一个数据表');
            setLoading(false);
            return;
          }
          // 准备需要新增的字段配置（只包含新增字段）
          const appendFieldConfigs = autoAddFields ? fieldConfigs.filter(config => {
            return !fields.some(f => f.name === config.name);
          }) : [];
          {
          const appendChunks = chunkArray(records, chunkSize);
          const startBatch = getStartBatch(appendChunks.length);
            let totalSuccess = 0;
            let totalError = 0;
            const allErrors: string[] = [];
          for (let idx = startBatch; idx < appendChunks.length; idx++) {
              // 检查是否已取消导入
              if (isImportCancelledRef.current) {
                console.log(`导入已取消，停止在第 ${idx + 1} 批`);
                setImportStatus('导入已取消');
                break;
              }
              
              const chunk = appendChunks[idx];
              const chunkStart = idx * chunkSize;
              setImportStatus(`正在导入数据... (${idx + 1}/${appendChunks.length})`);
              setImportProgress(20 + Math.floor((idx / appendChunks.length) * 60));
              try {
                // 在关键操作前再次检查取消标志
                if (isImportCancelledRef.current) {
                  console.log(`导入已取消，停止在第 ${idx + 1} 批（API调用前）`);
                  setImportStatus('导入已取消');
                  break;
                }
                
                await refreshPermissionPerBatch(idx, appendChunks.length);
                
                // 授权刷新后再次检查
                if (isImportCancelledRef.current) {
                  console.log(`导入已取消，停止在第 ${idx + 1} 批（授权刷新后）`);
                  setImportStatus('导入已取消');
                  break;
                }
                
                const appendRes = await scriptRunWithTimeout('appendRecordsToSheet', 600,
            selectedSheetId,
                  chunk,
            ignoreErrors,
                  appendFieldConfigs,
                  chunkStart
          );
                totalSuccess += appendRes.successCount || 0;
                totalError += appendRes.errorCount || 0;
                allErrors.push(...(appendRes.errors || []));
                
                // 每批处理完成后执行深度验证（最多等待5分钟）
                // 最后一段数据插入完成，不需要进行深度验证
                if (idx < appendChunks.length - 1) {
                  try {
                    setImportStatus(`正在验证表格状态... (${idx + 1}/${appendChunks.length})`);
                    const verificationResult = await scriptRunWithTimeout('performExtendedStabilityCheck', 300,
                      selectedSheetId,
                      totalSuccess,
                      '插入',
                      idx
                    );
                    
                    if (!verificationResult.success) {
                      // 关键修复：对于事件监听方式，如果验证失败，检查错误信息
                      const errorMsg = verificationResult.error || '';
                      const errorMsgLower = errorMsg.toLowerCase();
                      const isTimeoutOrNoEvent = (
                        (errorMsgLower.includes('等待') && errorMsgLower.includes('秒')) ||
                        errorMsgLower.includes('timeout') ||
                        errorMsgLower.includes('未收到') ||
                        errorMsgLower.includes('收到 0 个事件')
                      );
                      
                      if (isTimeoutOrNoEvent) {
                        // 超时或没有收到事件，可能表格已稳定，继续处理
                        console.log(`[深度验证] 第 ${idx + 1} 批验证超时或未收到事件（${errorMsg}），可能表格已稳定，继续处理`);
                      } else {
                        // 其他错误，保存断点并停止导入
                        const checkpoint = {
                          batchIndex: idx + 1,
                          processedCount: totalSuccess,
                          sheetId: selectedSheetId,
                          timestamp: Date.now()
                        };
                        localStorage.setItem('import_checkpoint', JSON.stringify(checkpoint));
                        
                        const errorMsg2 = `第 ${idx + 1} 批处理完成后，表格验证失败：${errorMsg || '未知错误'}\n\n已保存断点，可从第 ${idx + 2} 批继续导入。`;
                        console.error(errorMsg2);
                        alert(errorMsg2);
                        
                        setLoading(false);
                        setImportStatus('导入已停止（验证失败）');
                        isImportingRef.current = false;
                        isImportCancelledRef.current = true;
                        break;
                      }
                    } else {
                      console.log(`[深度验证] 第 ${idx + 1} 批验证成功，继续下一批`);
                    }
                  } catch (verifyErr: any) {
                    console.warn(`[深度验证] 第 ${idx + 1} 批验证过程出错: ${verifyErr.message}，继续处理...`);
                  }
                }
              } catch (err: any) {
                // 如果是取消错误，直接退出循环
                if (err instanceof Error && err.message === '导入已取消') {
                  console.log(`[取消处理] 导入已取消，停止在第 ${idx + 1} 批`);
                  setImportStatus('导入已取消');
                  setLoading(false);
                  isImportingRef.current = false;
                  break;
                }
                
                // 检查是否在错误处理过程中被取消
                if (isImportCancelledRef.current) {
                  console.log(`[取消处理] 在错误处理过程中检测到取消，停止在第 ${idx + 1} 批`);
                  setImportStatus('导入已取消');
                  setLoading(false);
                  isImportingRef.current = false;
                  break;
                }
                
                // 检测超时错误，设置取消标志并退出循环
                const errorMessage = err.message || String(err);
                const isTimeoutError = errorMessage.includes('timeout') ||
                                      errorMessage.includes('Timeout') ||
                                      errorMessage.includes('TIMEOUT') ||
                                      errorMessage.includes('超时');
                
                if (isTimeoutError) {
                  console.error(`[超时错误] 第 ${idx + 1} 批导入超时，设置取消标志以停止后续操作`);
                  isImportCancelledRef.current = true;
                  setLoading(false);
                  setImportStatus('导入超时，已停止');
                  isImportingRef.current = false;
                  break;
                }
                
                if (ignoreErrors) {
                  totalError += chunk.length;
                  allErrors.push(`第${idx + 1}批导入失败: ${err.message || err}`);
                  continue;
                }
                throw err;
              }
              
              // 批次间隔前检查是否已取消
              if (isImportCancelledRef.current) {
                console.log(`[取消处理] 在批次间隔前检测到取消，停止在第 ${idx + 1} 批`);
                setImportStatus('导入已取消');
                setLoading(false);
                isImportingRef.current = false;
                break;
              }
              
            await sleep(1000);
            
            // 批次间隔后再次检查是否已取消
            if (isImportCancelledRef.current) {
              console.log(`[取消处理] 在批次间隔后检测到取消，停止在第 ${idx + 1} 批`);
              setImportStatus('导入已取消');
              setLoading(false);
              isImportingRef.current = false;
              break;
            }
          }
          result = { successCount: totalSuccess, errorCount: totalError, errors: allErrors };
          }
          
          // 如果启用汇总，在导入后执行聚合合并
          if (enableAggregation && selectedSheetId) {
            // 汇总前检查是否已取消
            if (isImportCancelledRef.current) {
              console.log('导入已取消，跳过数据汇总');
              setImportStatus('导入已取消');
            } else {
            setImportStatus('正在汇总数据...');
            setImportProgress(90);
            try {
                const aggregationResult = await scriptRunWithTimeout('aggregateSheetData', 600,
                selectedSheetId,
                aggregationFields,
                sumFields,
                textSeparator || '；'
              );
              console.log(`数据汇总完成：原始 ${aggregationResult.originalCount} 条记录，合并后 ${aggregationResult.mergedCount} 条记录`);
              setImportStatus(`数据汇总完成：${aggregationResult.originalCount} → ${aggregationResult.mergedCount} 条`);
            } catch (error: any) {
                // 如果是取消错误，不显示错误提示
                if (error instanceof Error && error.message === '导入已取消') {
                  console.log('数据汇总已取消');
                  setImportStatus('导入已取消');
                } else {
              console.error('数据汇总失败:', error);
              alert(`数据汇总失败：${error.message}\n\n原始数据已导入成功`);
                }
              }
            }
          }
          break;

        case 'updateBlank':
          // 模式3：只添加新建数据
          if (!selectedSheetId || matchFields.length === 0) {
            alert('请选择数据表和匹配字段（至少1个，最多3个）');
            setLoading(false);
            return;
          }
          if (matchFields.length > 3) {
            alert('匹配字段最多只能选择3个');
            setLoading(false);
            return;
          }
          
          // 准备需要新增的字段配置（只包含新增字段）
          const insertFieldConfigs = fieldConfigs.filter(config => {
            return !fields.some(f => f.name === config.name);
          });
          {
            const chunks = chunkArray(records, chunkSize);
            const startBatch = getStartBatch(chunks.length);
            const isResumingFromCheckpoint = startBatch > 0;
            
            // 关键修复：只在第一次导入之前查询重复数据，如果从断点继续（startBatch > 0），跳过查询
            if (!enableAggregation && enableDuplicateMerge && !skipDuplicateCheck && !isResumingFromCheckpoint) {
              try {
                // 1. 查询数据表中的重复数据
                setImportStatus('正在查询数据表中的重复数据...');
                setQueryProgress({
                  isQuerying: true,
                  currentQuery: 0,
                  totalRecords: 0,
                  totalRecordsEstimate: 0,
                  matchedKeys: 0,
                  targetKeys: 0,
                  status: '正在查询数据表中的重复数据...',
                  sheetName: allSheets.find(s => s.id === selectedSheetId)?.name
                });
                
                const sheetDuplicateResult = await scriptRunWithTimeout('querySheetDuplicateRecords', 3600,
                  selectedSheetId,
                  matchFields,
                  duplicateMergeStrategy,
                  (duplicateMergeStrategy === 'largestField' || duplicateMergeStrategy === 'smallestField') ? duplicateMergeField : undefined
                );
                
                setQueryProgress({
                  isQuerying: false,
                  currentQuery: sheetDuplicateResult.totalQueries,
                  totalRecords: sheetDuplicateResult.totalRecords,
                  totalRecordsEstimate: sheetDuplicateResult.totalRecords,
                  matchedKeys: sheetDuplicateResult.uniqueMatchKeys,
                  targetKeys: sheetDuplicateResult.uniqueMatchKeys,
                  status: `查询完成，数据表"${sheetDuplicateResult.sheetName}"共 ${sheetDuplicateResult.totalRecords} 条记录，发现 ${sheetDuplicateResult.duplicateGroups.length} 组重复数据`,
                  sheetName: sheetDuplicateResult.sheetName
                });
                
                console.log('数据表重复数据查询结果:', sheetDuplicateResult);
                
                // 如果有重复数据，显示UI让用户选择删除
                if (sheetDuplicateResult.duplicateGroups.length > 0) {
                  // 存储重复数据详情，显示UI
                  setDuplicateData(sheetDuplicateResult);
                  setImportStatus(`发现 ${sheetDuplicateResult.duplicateGroups.length} 组重复数据，共 ${sheetDuplicateResult.duplicateCount} 条重复记录，请选择要删除的记录`);
                  setLoading(false);
                  // 等待用户操作，不继续执行导入
                  return;
                } else {
                  // 没有重复数据，自动继续处理导入数据
                  console.log('[自动继续] 未发现重复数据，自动继续导入...');
                  setDuplicateData(null);
                  setImportStatus('未发现重复数据，正在继续导入...');
                }
              } catch (err: any) {
                console.error('查询重复数据失败:', err);
                // 如果是取消错误，直接返回，不继续执行导入
                if (err instanceof Error && err.message === '导入已取消') {
                  setImportStatus('导入已取消');
                  setLoading(false);
                  isImportCancelledRef.current = false;
                  isImportingRef.current = false;
                  return;
                }
                alert(`查询重复数据失败: ${err.message}\n\n将继续执行导入，但不会进行重复数据处理`);
              }
            } else if (isResumingFromCheckpoint) {
              // 从断点继续，跳过重复数据查询
              console.log(`[断点继续] 从第 ${startBatch + 1} 批继续导入，跳过重复数据查询`);
            }
            let totalSuccess = 0;
            let totalError = 0;
            let totalSkipped = 0;
            const allErrors: string[] = [];
            for (let idx = startBatch; idx < chunks.length; idx++) {
              // 检查是否已取消导入
              if (isImportCancelledRef.current) {
                console.log(`导入已取消，停止在第 ${idx + 1} 批`);
                setImportStatus('导入已取消');
                break;
              }
              
              const chunk = chunks[idx];
              const chunkStart = idx * chunkSize;
              setImportStatus(`正在导入数据... (${idx + 1}/${chunks.length})`);
              setImportProgress(20 + Math.floor((idx / chunks.length) * 60));
              try {
                // 在关键操作前再次检查取消标志
                if (isImportCancelledRef.current) {
                  console.log(`导入已取消，停止在第 ${idx + 1} 批（API调用前）`);
                  setImportStatus('导入已取消');
                  break;
                }
                
                await refreshPermissionPerBatch(idx, chunks.length);
                
                // 授权刷新后再次检查
                if (isImportCancelledRef.current) {
                  console.log(`导入已取消，停止在第 ${idx + 1} 批（授权刷新后）`);
                  setImportStatus('导入已取消');
                  break;
                }
                
                // 对于模式3（只添加新数据），需要查询所有记录进行匹配
                // 不设置整体超时，因为每次查询（100条）都有独立的超时检测
                // 设置一个很大的超时时间（1小时）作为安全上限，避免无限等待
                const res = await scriptRunWithTimeout('insertOnlyNewRecords', 3600,
            selectedSheetId,
            matchFields,
                  chunk,
            ignoreErrors,
                  insertFieldConfigs,
                  chunkStart,
                  !enableAggregation && enableDuplicateMerge ? duplicateMergeStrategy : undefined,
                  !enableAggregation && enableDuplicateMerge && (duplicateMergeStrategy === 'largestField' || duplicateMergeStrategy === 'smallestField') ? duplicateMergeField : undefined
          );
                totalSuccess += res.successCount || 0;
                totalError += res.errorCount || 0;
                totalSkipped += res.skippedCount || 0;
                allErrors.push(...(res.errors || []));
                
                // 每批处理完成后执行深度验证（最多等待5分钟）
                // 最后一段数据插入完成，不需要进行深度验证
                if (idx < chunks.length - 1) {
                  try {
                    setImportStatus(`正在验证表格状态... (${idx + 1}/${chunks.length})`);
                    const verificationResult = await scriptRunWithTimeout('performExtendedStabilityCheck', 300,
                      selectedSheetId,
                      totalSuccess,
                      '插入',
                      idx
                    );
                    
                    if (!verificationResult.success) {
                      // 关键修复：对于事件监听方式，如果验证失败，检查错误信息
                      const errorMsg = verificationResult.error || '';
                      const errorMsgLower = errorMsg.toLowerCase();
                      const isTimeoutOrNoEvent = (
                        (errorMsgLower.includes('等待') && errorMsgLower.includes('秒')) ||
                        errorMsgLower.includes('timeout') ||
                        errorMsgLower.includes('未收到') ||
                        errorMsgLower.includes('收到 0 个事件')
                      );
                      
                      if (isTimeoutOrNoEvent) {
                        // 超时或没有收到事件，可能表格已稳定，继续处理
                        console.log(`[深度验证] 第 ${idx + 1} 批验证超时或未收到事件（${errorMsg}），可能表格已稳定，继续处理`);
                      } else {
                        // 其他错误，保存断点并停止导入
                        const checkpoint = {
                          batchIndex: idx + 1,
                          processedCount: totalSuccess,
                          sheetId: selectedSheetId,
                          timestamp: Date.now()
                        };
                        localStorage.setItem('import_checkpoint', JSON.stringify(checkpoint));
                        
                        const errorMsg2 = `第 ${idx + 1} 批处理完成后，表格验证失败：${errorMsg || '未知错误'}\n\n已保存断点，可从第 ${idx + 2} 批继续导入。`;
                        console.error(errorMsg2);
                        alert(errorMsg2);
                        
                        setLoading(false);
                        setImportStatus('导入已停止（验证失败）');
                        isImportingRef.current = false;
                        isImportCancelledRef.current = true;
                        break;
                      }
                    } else {
                      console.log(`[深度验证] 第 ${idx + 1} 批验证成功，继续下一批`);
                    }
                  } catch (verifyErr: any) {
                    console.warn(`[深度验证] 第 ${idx + 1} 批验证过程出错: ${verifyErr.message}，继续处理...`);
                  }
                }
              } catch (err: any) {
                // 如果是取消错误，直接退出循环
                if (err instanceof Error && err.message === '导入已取消') {
                  console.log(`[取消处理] 导入已取消，停止在第 ${idx + 1} 批`);
                  setImportStatus('导入已取消');
                  setLoading(false);
                  isImportingRef.current = false;
                  break;
                }
                
                // 检查是否在错误处理过程中被取消
                if (isImportCancelledRef.current) {
                  console.log(`[取消处理] 在错误处理过程中检测到取消，停止在第 ${idx + 1} 批`);
                  setImportStatus('导入已取消');
                  setLoading(false);
                  isImportingRef.current = false;
                  break;
                }
                
                if (ignoreErrors) {
                  totalError += chunk.length;
                  allErrors.push(`第${idx + 1}批导入失败: ${err.message || err}`);
                  continue;
                }
                throw err;
              }
            // 批次间隔前检查是否已取消
            if (isImportCancelledRef.current) {
              console.log(`[取消处理] 在批次间隔前检测到取消，停止在第 ${idx + 1} 批`);
              setImportStatus('导入已取消');
              setLoading(false);
              isImportingRef.current = false;
              break;
            }
            
            await sleep(1000);
            
            // 批次间隔后再次检查是否已取消
            if (isImportCancelledRef.current) {
              console.log(`[取消处理] 在批次间隔后检测到取消，停止在第 ${idx + 1} 批`);
              setImportStatus('导入已取消');
              setLoading(false);
              isImportingRef.current = false;
              break;
            }
            }
            result = { successCount: totalSuccess, errorCount: totalError, skippedCount: totalSkipped, errors: allErrors };
          }
          
          // 如果启用汇总，在导入后执行聚合合并
          if (enableAggregation && selectedSheetId) {
            // 汇总前检查是否已取消
            if (isImportCancelledRef.current) {
              console.log('导入已取消，跳过数据汇总');
              setImportStatus('导入已取消');
            } else {
            setImportStatus('正在汇总数据...');
            setImportProgress(90);
            try {
                const aggregationResult = await scriptRunWithTimeout('aggregateSheetData', 600,
                selectedSheetId,
                aggregationFields,
                sumFields,
                textSeparator || '；'
              );
              console.log(`数据汇总完成：原始 ${aggregationResult.originalCount} 条记录，合并后 ${aggregationResult.mergedCount} 条记录`);
              setImportStatus(`数据汇总完成：${aggregationResult.originalCount} → ${aggregationResult.mergedCount} 条`);
            } catch (error: any) {
                // 如果是取消错误，不显示错误提示
                if (error instanceof Error && error.message === '导入已取消') {
                  console.log('数据汇总已取消');
                  setImportStatus('导入已取消');
                } else {
              console.error('数据汇总失败:', error);
              alert(`数据汇总失败：${error.message}\n\n原始数据已导入成功`);
                }
              }
            }
          }
          break;

        case 'updateExisting':
          if (!selectedSheetId || matchFields.length === 0) {
            alert('请选择数据表和匹配字段（至少1个，最多3个）');
            setLoading(false);
            return;
          }
          
          if (matchFields.length > 3) {
            alert('匹配字段最多只能选择3个');
            setLoading(false);
            return;
          }
          // 准备需要新增的字段配置（只包含新增字段）
          const updateFieldConfigs = fieldConfigs.filter(config => {
            return !fields.some(f => f.name === config.name);
          });
          // 模式4：只更新已有数据（排除匹配字段）
          {
            const chunks = chunkArray(records, chunkSize);
            const startBatch = getStartBatch(chunks.length);
            const isResumingFromCheckpoint = startBatch > 0;
            
            // 关键修复：只在第一次导入之前查询重复数据，如果从断点继续（startBatch > 0），跳过查询
            if (!enableAggregation && enableDuplicateMerge && !skipDuplicateCheck && !isResumingFromCheckpoint) {
              try {
                // 1. 查询数据表中的重复数据
                setImportStatus('正在查询数据表中的重复数据...');
                setQueryProgress({
                  isQuerying: true,
                  currentQuery: 0,
                  totalRecords: 0,
                  totalRecordsEstimate: 0,
                  matchedKeys: 0,
                  targetKeys: 0,
                  status: '正在查询数据表中的重复数据...',
                  sheetName: allSheets.find(s => s.id === selectedSheetId)?.name
                });
                
                const sheetDuplicateResult = await scriptRunWithTimeout('querySheetDuplicateRecords', 3600,
                  selectedSheetId,
                  matchFields,
                  duplicateMergeStrategy,
                  (duplicateMergeStrategy === 'largestField' || duplicateMergeStrategy === 'smallestField') ? duplicateMergeField : undefined
                );
                
                setQueryProgress({
                  isQuerying: false,
                  currentQuery: sheetDuplicateResult.totalQueries,
                  totalRecords: sheetDuplicateResult.totalRecords,
                  totalRecordsEstimate: sheetDuplicateResult.totalRecords,
                  matchedKeys: sheetDuplicateResult.uniqueMatchKeys,
                  targetKeys: sheetDuplicateResult.uniqueMatchKeys,
                  status: `查询完成，数据表"${sheetDuplicateResult.sheetName}"共 ${sheetDuplicateResult.totalRecords} 条记录，发现 ${sheetDuplicateResult.duplicateGroups.length} 组重复数据`,
                  sheetName: sheetDuplicateResult.sheetName
                });
                
                console.log('数据表重复数据查询结果:', sheetDuplicateResult);
                
                // 如果有重复数据，显示UI让用户选择删除
                if (sheetDuplicateResult.duplicateGroups.length > 0) {
                  // 存储重复数据详情，显示UI
                  setDuplicateData(sheetDuplicateResult);
                  setImportStatus(`发现 ${sheetDuplicateResult.duplicateGroups.length} 组重复数据，共 ${sheetDuplicateResult.duplicateCount} 条重复记录，请选择要删除的记录`);
                  setLoading(false);
                  // 等待用户操作，不继续执行导入
                  return;
                } else {
                  // 没有重复数据，自动继续处理导入数据
                  console.log('[自动继续] 未发现重复数据，自动继续更新...');
                  setDuplicateData(null);
                  setImportStatus('未发现重复数据，正在继续更新...');
                }
              } catch (err: any) {
                console.error('查询重复数据失败:', err);
                // 如果是取消错误，直接返回，不继续执行导入
                if (err instanceof Error && err.message === '导入已取消') {
                  setImportStatus('导入已取消');
                  setLoading(false);
                  isImportCancelledRef.current = false;
                  isImportingRef.current = false;
                  return;
                }
                alert(`查询重复数据失败: ${err.message}\n\n将继续执行导入，但不会进行重复数据处理`);
              }
            } else if (isResumingFromCheckpoint) {
              // 从断点继续，跳过重复数据查询
              console.log(`[断点继续] 从第 ${startBatch + 1} 批继续导入，跳过重复数据查询`);
            }
            let totalSuccess = 0;
            let totalError = 0;
            let totalSkipped = 0;
            const allErrors: string[] = [];
            for (let idx = startBatch; idx < chunks.length; idx++) {
              // 检查是否已取消导入
              if (isImportCancelledRef.current) {
                console.log(`导入已取消，停止在第 ${idx + 1} 批`);
                setImportStatus('导入已取消');
                break;
              }
              
              const chunk = chunks[idx];
              const chunkStart = idx * chunkSize;
              setImportStatus(`正在导入数据... (${idx + 1}/${chunks.length})`);
              setImportProgress(20 + Math.floor((idx / chunks.length) * 60));
              try {
                // 在关键操作前再次检查取消标志
                if (isImportCancelledRef.current) {
                  console.log(`导入已取消，停止在第 ${idx + 1} 批（API调用前）`);
                  setImportStatus('导入已取消');
                  break;
                }
                
                await refreshPermissionPerBatch(idx, chunks.length);
                
                // 授权刷新后再次检查
                if (isImportCancelledRef.current) {
                  console.log(`导入已取消，停止在第 ${idx + 1} 批（授权刷新后）`);
                  setImportStatus('导入已取消');
                  break;
                }
                
                // 对于模式4（更新已有数据），需要查询所有记录进行匹配
                // 不设置整体超时，因为每次查询（100条）都有独立的超时检测
                // 设置一个很大的超时时间（1小时）作为安全上限，避免无限等待
                const res = await scriptRunWithTimeout('updateOnlyExistingRecords', 3600,
            selectedSheetId,
            matchFields,
                  chunk,
            ignoreErrors,
                  updateFieldConfigs,
                  chunkStart,
                  !enableAggregation && enableDuplicateMerge ? duplicateMergeStrategy : undefined,
                  !enableAggregation && enableDuplicateMerge && (duplicateMergeStrategy === 'largestField' || duplicateMergeStrategy === 'smallestField') ? duplicateMergeField : undefined
          );
                totalSuccess += res.successCount || 0;
                totalError += res.errorCount || 0;
                totalSkipped += res.skippedCount || 0;
                allErrors.push(...(res.errors || []));
                
                // 每批处理完成后执行深度验证（最多等待5分钟）
                // 最后一段数据插入完成，不需要进行深度验证
                if (idx < chunks.length - 1) {
                  try {
                    setImportStatus(`正在验证表格状态... (${idx + 1}/${chunks.length})`);
                    const verificationResult = await scriptRunWithTimeout('performExtendedStabilityCheck', 300,
                      selectedSheetId,
                      totalSuccess,
                      '更新',
                      idx
                    );
                  
                  if (!verificationResult.success) {
                    // 关键修复：对于事件监听方式，如果验证失败，检查错误信息
                    const errorMsg = verificationResult.error || '';
                    const errorMsgLower = errorMsg.toLowerCase();
                    const isTimeoutOrNoEvent = (
                      (errorMsgLower.includes('等待') && errorMsgLower.includes('秒')) ||
                      errorMsgLower.includes('timeout') ||
                      errorMsgLower.includes('未收到') ||
                      errorMsgLower.includes('收到 0 个事件')
                    );
                    
                    if (isTimeoutOrNoEvent) {
                      // 超时或没有收到事件，可能表格已稳定，继续处理
                      console.log(`[深度验证] 第 ${idx + 1} 批验证超时或未收到事件（${errorMsg}），可能表格已稳定，继续处理`);
                    } else {
                      // 其他错误，保存断点并停止导入
                      const checkpoint = {
                        batchIndex: idx + 1,
                        processedCount: totalSuccess,
                        sheetId: selectedSheetId,
                        timestamp: Date.now()
                      };
                      localStorage.setItem('import_checkpoint', JSON.stringify(checkpoint));
                      
                      const errorMsg2 = `第 ${idx + 1} 批处理完成后，表格验证失败：${errorMsg || '未知错误'}\n\n已保存断点，可从第 ${idx + 2} 批继续导入。`;
                      console.error(errorMsg2);
                      alert(errorMsg2);
                      
                      setLoading(false);
                      setImportStatus('导入已停止（验证失败）');
                      isImportingRef.current = false;
                      isImportCancelledRef.current = true;
                      break;
                    }
                  } else {
                    console.log(`[深度验证] 第 ${idx + 1} 批验证成功，继续下一批`);
                  }
                  } catch (verifyErr: any) {
                    console.warn(`[深度验证] 第 ${idx + 1} 批验证过程出错: ${verifyErr.message}，继续处理...`);
                  }
                }
              } catch (err: any) {
                // 如果是取消错误，直接退出循环
                if (err instanceof Error && err.message === '导入已取消') {
                  console.log(`[取消处理] 导入已取消，停止在第 ${idx + 1} 批`);
                  setImportStatus('导入已取消');
                  setLoading(false);
                  isImportingRef.current = false;
                  break;
                }
                
                // 检查是否在错误处理过程中被取消
                if (isImportCancelledRef.current) {
                  console.log(`[取消处理] 在错误处理过程中检测到取消，停止在第 ${idx + 1} 批`);
                  setImportStatus('导入已取消');
                  setLoading(false);
                  isImportingRef.current = false;
                  break;
                }
                
                // 检测超时错误，设置取消标志并退出循环
                const errorMessage = err.message || String(err);
                const isTimeoutError = errorMessage.includes('timeout') ||
                                      errorMessage.includes('Timeout') ||
                                      errorMessage.includes('TIMEOUT') ||
                                      errorMessage.includes('超时');
                
                if (isTimeoutError) {
                  console.error(`[超时错误] 第 ${idx + 1} 批导入超时，设置取消标志以停止后续操作`);
                  isImportCancelledRef.current = true;
                  setLoading(false);
                  setImportStatus('导入超时，已停止');
                  isImportingRef.current = false;
                  break;
                }
                
                if (ignoreErrors) {
                  totalError += chunk.length;
                  allErrors.push(`第${idx + 1}批导入失败: ${err.message || err}`);
                  continue;
                }
                throw err;
              }
              // 模式3：批次之间增加延迟，让数据表有时间同步（第一批插入后，第二批查询前需要等待）
              // 第一批后等待2秒，后续批次等待1秒
              const batchDelay = idx === 0 ? 2000 : 1000;
              await sleep(batchDelay);
            }
            result = { successCount: totalSuccess, errorCount: totalError, skippedCount: totalSkipped, errors: allErrors };
          }
          
          // 如果启用汇总，在导入后执行聚合合并
          if (enableAggregation && selectedSheetId) {
            // 汇总前检查是否已取消
            if (isImportCancelledRef.current) {
              console.log('导入已取消，跳过数据汇总');
              setImportStatus('导入已取消');
            } else {
            setImportStatus('正在汇总数据...');
            setImportProgress(90);
            try {
                const aggregationResult = await scriptRunWithTimeout('aggregateSheetData', 600,
                selectedSheetId,
                aggregationFields,
                sumFields,
                textSeparator || '；'
              );
              console.log(`数据汇总完成：原始 ${aggregationResult.originalCount} 条记录，合并后 ${aggregationResult.mergedCount} 条记录`);
              setImportStatus(`数据汇总完成：${aggregationResult.originalCount} → ${aggregationResult.mergedCount} 条`);
            } catch (error: any) {
                // 如果是取消错误，不显示错误提示
                if (error instanceof Error && error.message === '导入已取消') {
                  console.log('数据汇总已取消');
                  setImportStatus('导入已取消');
                } else {
              console.error('数据汇总失败:', error);
              alert(`数据汇总失败：${error.message}\n\n原始数据已导入成功`);
                }
              }
            }
          }
          break;

        case 'updateBoth':
          if (!selectedSheetId || matchFields.length === 0) {
            alert('请选择数据表和匹配字段（至少1个，最多3个）');
            setLoading(false);
            return;
          }
          
          // 如果启用了重复数据合并，先查询数据表的重复数据（除非已跳过）
          if (!enableAggregation && enableDuplicateMerge && !skipDuplicateCheck) {
            try {
              // 1. 查询数据表中的重复数据
              setImportStatus('正在查询数据表中的重复数据...');
              setQueryProgress({
                isQuerying: true,
                currentQuery: 0,
                totalRecords: 0,
                totalRecordsEstimate: 0,
                matchedKeys: 0,
                targetKeys: 0,
                status: '正在查询数据表中的重复数据...',
                sheetName: allSheets.find(s => s.id === selectedSheetId)?.name
              });
              
              const sheetDuplicateResult = await scriptRunWithTimeout('querySheetDuplicateRecords', 3600,
                selectedSheetId,
                matchFields,
                duplicateMergeStrategy,
                (duplicateMergeStrategy === 'largestField' || duplicateMergeStrategy === 'smallestField') ? duplicateMergeField : undefined
              );
              
              setQueryProgress({
                isQuerying: false,
                currentQuery: sheetDuplicateResult.totalQueries,
                totalRecords: sheetDuplicateResult.totalRecords,
                totalRecordsEstimate: sheetDuplicateResult.totalRecords,
                matchedKeys: sheetDuplicateResult.uniqueMatchKeys,
                targetKeys: sheetDuplicateResult.uniqueMatchKeys,
                status: `查询完成，数据表"${sheetDuplicateResult.sheetName}"共 ${sheetDuplicateResult.totalRecords} 条记录，发现 ${sheetDuplicateResult.duplicateGroups.length} 组重复数据`,
                sheetName: sheetDuplicateResult.sheetName
              });
              
              console.log('数据表重复数据查询结果:', sheetDuplicateResult);
              
              // 如果有重复数据，显示UI让用户选择删除
              if (sheetDuplicateResult.duplicateGroups.length > 0) {
                // 存储重复数据详情，显示UI
                setDuplicateData(sheetDuplicateResult);
                setImportStatus(`发现 ${sheetDuplicateResult.duplicateGroups.length} 组重复数据，共 ${sheetDuplicateResult.duplicateCount} 条重复记录，请选择要删除的记录`);
                setLoading(false);
                // 等待用户操作，不继续执行导入
                return;
              } else {
                // 没有重复数据，自动继续处理导入数据
                console.log('[自动继续] 未发现重复数据，自动继续导入和更新...');
                setDuplicateData(null);
                setImportStatus('未发现重复数据，正在继续导入和更新...');
              }
            } catch (err: any) {
              console.error('查询重复数据失败:', err);
              // 如果是取消错误，直接返回，不继续执行导入
              if (err instanceof Error && err.message === '导入已取消') {
                setImportStatus('导入已取消');
                setLoading(false);
                isImportCancelledRef.current = false;
                isImportingRef.current = false;
                return;
              }
              alert(`查询重复数据失败: ${err.message}\n\n将继续执行导入，但不会进行重复数据处理`);
            }
          }
          if (matchFields.length > 3) {
            alert('匹配字段最多只能选择3个');
            setLoading(false);
            return;
          }
          // 准备需要新增的字段配置（只包含新增字段）
          const bothFieldConfigs = fieldConfigs.filter(config => {
            return !fields.some(f => f.name === config.name);
          });
          // 模式5：更新并添加（分块执行）
          {
            const chunks = chunkArray(records, chunkSize);
            const startBatch = getStartBatch(chunks.length);
            let totalSuccess = 0;
            let totalError = 0;
            let totalSkipped = 0;
            const allErrors: string[] = [];
            for (let idx = startBatch; idx < chunks.length; idx++) {
              // 检查是否已取消导入
              if (isImportCancelledRef.current) {
                console.log(`导入已取消，停止在第 ${idx + 1} 批`);
                setImportStatus('导入已取消');
                break;
              }
              
              const chunk = chunks[idx];
              const chunkStart = idx * chunkSize;
              setImportStatus(`正在导入数据... (${idx + 1}/${chunks.length})`);
              setImportProgress(20 + Math.floor((idx / chunks.length) * 60));
              try {
                // 在关键操作前再次检查取消标志
                if (isImportCancelledRef.current) {
                  console.log(`导入已取消，停止在第 ${idx + 1} 批（API调用前）`);
                  setImportStatus('导入已取消');
                  break;
                }
                
                await refreshPermissionPerBatch(idx, chunks.length);
                
                // 授权刷新后再次检查
                if (isImportCancelledRef.current) {
                  console.log(`导入已取消，停止在第 ${idx + 1} 批（授权刷新后）`);
                  setImportStatus('导入已取消');
                  break;
                }
                
                // 模式5优化：使用专用函数，只构建一次索引，同时处理更新和插入
                // 这样可以将索引构建次数从每个分块2次降低到1次，大幅提升性能
                // 关键修复：传递分块导入信息，确保每个chunk能看到之前插入的记录
                const result = await scriptRunWithTimeout('updateAndInsertRecords', 3600,
            selectedSheetId,
            matchFields,
                  chunk,
            ignoreErrors,
                  bothFieldConfigs,
                  chunkStart,
                  !enableAggregation && enableDuplicateMerge ? duplicateMergeStrategy : undefined,
                  !enableAggregation && enableDuplicateMerge && (duplicateMergeStrategy === 'largestField' || duplicateMergeStrategy === 'smallestField') ? duplicateMergeField : undefined,
                  true, // isChunkedImport: 标记为分块导入
                  idx // chunkIndex: 当前chunk索引
                );
                
                totalSuccess += result.successCount || 0;
                totalError += result.errorCount || 0;
                totalSkipped += result.skippedCount || 0;
                allErrors.push(...(result.errors || []));
                
                // 每批处理完成后执行深度验证（最多等待5分钟）
                // 最后一段数据插入完成，不需要进行深度验证
                if (idx < chunks.length - 1) {
                  try {
                    setImportStatus(`正在验证表格状态... (${idx + 1}/${chunks.length})`);
                    console.log(`[分段验证] 第 ${idx + 1}/${chunks.length} 批处理完成，开始验证表格状态...`);
                    const verificationResult = await scriptRunWithTimeout('performExtendedStabilityCheck', 300,
                      selectedSheetId,
                      totalSuccess,
                      '更新',
                      idx
                    );
                  
                  if (!verificationResult.success) {
                    // 关键修复：对于事件监听方式，如果验证失败，检查错误信息
                    const errorMsg = verificationResult.error || '';
                    const errorMsgLower = errorMsg.toLowerCase();
                    const isTimeoutOrNoEvent = (
                      (errorMsgLower.includes('等待') && errorMsgLower.includes('秒')) ||
                      errorMsgLower.includes('timeout') ||
                      errorMsgLower.includes('未收到') ||
                      errorMsgLower.includes('收到 0 个事件')
                    );
                    
                    if (isTimeoutOrNoEvent) {
                      // 超时或没有收到事件，可能表格已稳定，继续处理
                      console.log(`[分段验证] 第 ${idx + 1} 批验证超时或未收到事件（${errorMsg}），可能表格已稳定，继续处理`);
                    } else {
                      // 其他错误，保存断点并停止导入
                      const checkpoint = {
                        batchIndex: idx + 1,
                        processedCount: totalSuccess,
                        sheetId: selectedSheetId,
                        timestamp: Date.now()
                      };
                      localStorage.setItem('import_checkpoint', JSON.stringify(checkpoint));
                      
                      const errorMsg2 = `第 ${idx + 1} 批处理完成后，表格验证失败：${errorMsg || '未知错误'}\n\n已保存断点，可从第 ${idx + 2} 批继续导入。`;
                      console.error(`[分段验证失败] ${errorMsg2}`);
                      alert(errorMsg2);
                      
                      setLoading(false);
                      setImportStatus('导入已停止（验证失败）');
                      isImportingRef.current = false;
                      isImportCancelledRef.current = true;
                      break;
                    }
                  } else {
                    console.log(`[分段验证成功] 第 ${idx + 1} 批验证成功，表格状态正常，继续下一批`);
                  }
                  } catch (verifyErr: any) {
                    // 关键修复：如果验证过程出错，检查是否是"表格不在计算状态"的错误
                    const errorMessage = verifyErr.message || String(verifyErr);
                    const errorMessageLower = errorMessage.toLowerCase();
                    const isNotCalculatingError = (
                      errorMessageLower.includes('不在计算') ||
                      errorMessageLower.includes('not calculating') ||
                      errorMessageLower.includes('计算已完成') ||
                      errorMessageLower.includes('calculation completed')
                    );
                    
                    if (isNotCalculatingError) {
                      // 表格不在计算状态，认为验证成功，继续处理
                      console.log(`[分段验证] 第 ${idx + 1} 批验证过程检测到表格不在计算状态（${errorMessage}），认为表格已就绪，继续处理`);
                    } else {
                      // 其他错误，记录警告但继续处理
                      console.warn(`[分段验证] 第 ${idx + 1} 批验证过程出错: ${errorMessage}，继续处理...`);
                    }
                  }
                }
              } catch (err: any) {
                // 如果是取消错误，直接退出循环
                if (err instanceof Error && err.message === '导入已取消') {
                  console.log(`[取消处理] 导入已取消，停止在第 ${idx + 1} 批`);
                  setImportStatus('导入已取消');
                  setLoading(false);
                  isImportingRef.current = false;
                  break;
                }
                
                // 检查是否在错误处理过程中被取消
                if (isImportCancelledRef.current) {
                  console.log(`[取消处理] 在错误处理过程中检测到取消，停止在第 ${idx + 1} 批`);
                  setImportStatus('导入已取消');
                  setLoading(false);
                  isImportingRef.current = false;
                  break;
                }
                
                if (ignoreErrors) {
                  totalError += chunk.length;
                  allErrors.push(`第${idx + 1}批导入失败: ${err.message || err}`);
                  continue;
                }
                throw err;
              }
              
              // 批次间隔前检查是否已取消
              if (isImportCancelledRef.current) {
                console.log(`[取消处理] 在批次间隔前检测到取消，停止在第 ${idx + 1} 批`);
                setImportStatus('导入已取消');
                setLoading(false);
                isImportingRef.current = false;
                break;
              }
              
              await sleep(1000);
              
              // 批次间隔后再次检查是否已取消
              if (isImportCancelledRef.current) {
                console.log(`[取消处理] 在批次间隔后检测到取消，停止在第 ${idx + 1} 批`);
                setImportStatus('导入已取消');
                setLoading(false);
                isImportingRef.current = false;
                break;
              }
            }
          
          // 如果启用汇总，在导入后执行聚合合并
          if (enableAggregation && selectedSheetId) {
              // 汇总前检查是否已取消
              if (isImportCancelledRef.current) {
                console.log('导入已取消，跳过数据汇总');
                setImportStatus('导入已取消');
              } else {
            setImportStatus('正在汇总数据...');
            setImportProgress(90);
            try {
                  const aggregationResult = await scriptRunWithTimeout('aggregateSheetData', 600,
                selectedSheetId,
                aggregationFields,
                sumFields,
                textSeparator || '；'
              );
              console.log(`数据汇总完成：原始 ${aggregationResult.originalCount} 条记录，合并后 ${aggregationResult.mergedCount} 条记录`);
              setImportStatus(`数据汇总完成：${aggregationResult.originalCount} → ${aggregationResult.mergedCount} 条`);
            } catch (error: any) {
                  // 如果是取消错误，不显示错误提示
                  if (error instanceof Error && error.message === '导入已取消') {
                    console.log('数据汇总已取消');
                    setImportStatus('导入已取消');
                  } else {
              console.error('数据汇总失败:', error);
              alert(`数据汇总失败：${error.message}\n\n原始数据已导入成功`);
            }
          }
              }
            }

          result = {
              successCount: totalSuccess,
              errorCount: totalError,
              skippedCount: totalSkipped,
              errors: allErrors
          };
          }
          break;
      }

      // 如果已取消，不显示完成状态
      if (!isImportCancelledRef.current) {
      setImportProgress(100);
      setImportStatus('导入完成');
      }
      
      // 停止捕获日志
      stopLogCapture();
        restoreConsole();
      
      // 如果已取消，直接返回，不显示结果
      if (isImportCancelledRef.current) {
        setLoading(false);
        // 重置取消状态和导入标志，允许用户重新导入
        isImportCancelledRef.current = false;
        isImportingRef.current = false;
        return;
      }

      // 构建详细的导入结果消息
      let resultMessage = '';
      if (result.successCount > 0) {
        resultMessage += `✅ 成功导入 ${result.successCount} 条记录`;
        if (result.skippedCount > 0) {
          resultMessage += `，跳过 ${result.skippedCount} 条记录`;
        }
        console.log(resultMessage);
      }
      
      if (result.errorCount > 0) {
        const errorMsg = `❌ 有 ${result.errorCount} 条记录导入失败`;
        console.error(errorMsg);
        let detailMessage = errorMsg;
        if (result.errors && result.errors.length > 0) {
          // 保存错误行日志（包含真实行号）
          setErrorLogLines(result.errors);
          detailMessage += '\n\n错误详情：\n';
          // 显示前10个错误
          const displayErrors = result.errors.slice(0, 10);
          displayErrors.forEach((err: string, idx: number) => {
            detailMessage += `${idx + 1}. ${err}\n`;
          });
          if (result.errors.length > 10) {
            detailMessage += `\n... 还有 ${result.errors.length - 10} 个错误（详见控制台）`;
          }
          // 生成错误日志文件，包含全部错误行
          try {
            const logContent = result.errors.join('\n');
            const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const ts = new Date().toISOString().replace(/[:.]/g, '-');
            a.href = url;
            a.download = `import-errors-${ts}.log`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            console.log('错误日志已生成并触发下载');
          } catch (logErr) {
            console.warn('生成错误日志失败:', logErr);
          }
        } else {
          detailMessage += '\n\n请检查：\n1. 字段映射是否正确\n2. 数据类型是否匹配\n3. 必填字段是否有值\n4. 数据格式是否符合要求';
        }
        alert(detailMessage);
      }
      
      if (result.successCount === 0 && result.errorCount === 0) {
        const noDataMsg = '⚠️ 没有数据被导入\n\n可能的原因：\n1. 所有记录都已存在（模式3）\n2. 没有匹配的记录（模式4）\n3. 字段映射全部选择"不导入"\n4. 数据为空或格式不正确';
        console.warn(noDataMsg);
        alert(noDataMsg);
      } else if (result.successCount > 0 && result.errorCount === 0) {
        // 只有成功时才显示成功提示
        alert(resultMessage);
        // 清空旧的错误日志，避免后续下载到历史错误
        setErrorLogLines([]);
      }

      // 重新加载数据表
      await loadActiveSheet();

      // 导入完成（无论成功还是失败），重置取消状态和导入标志
      isImportCancelledRef.current = false;
      isImportingRef.current = false;

    } catch (error: any) {
      const errorMessage = error.message || '未知错误';
      console.error(`❌ 导入失败:`, error);
      
      // 如果是取消错误，不显示错误提示，只重置状态
      if (error instanceof Error && error.message === '导入已取消') {
        console.log('导入已取消');
        setLoading(false);
        // 重置取消状态和导入标志，允许用户重新导入
        isImportCancelledRef.current = false;
        isImportingRef.current = false;
        return;
      }
      
      // 检测超时错误，设置取消标志以停止后续 API 调用
      const isTimeoutError = errorMessage.includes('timeout') ||
                            errorMessage.includes('Timeout') ||
                            errorMessage.includes('TIMEOUT') ||
                            errorMessage.includes('超时');
      
      if (isTimeoutError) {
        console.error(`[超时错误] 导入超时，设置取消标志以停止后续操作`);
        isImportCancelledRef.current = true;
        setLoading(false);
        setImportStatus('导入超时，已停止');
        isImportingRef.current = false; // 重置导入标志
      }
      
      // 构建详细的错误信息
      let detailError = `导入失败: ${errorMessage}`;
      
      // 检查是否是新增字段相关错误
      if (errorMessage.includes('字段') || errorMessage.includes('field')) {
        const hasNewFields = fieldMappings.some(m => m.isNewField);
        if (hasNewFields) {
          detailError += '\n\n检测到新增字段，可能的原因：\n';
          detailError += '1. 字段名称包含特殊字符\n';
          detailError += '2. 字段类型配置不正确\n';
          detailError += '3. 字段已存在但名称不同\n';
          detailError += '4. 权限不足，无法创建字段\n\n';
          detailError += '建议：\n';
          detailError += '- 检查字段映射中的新增字段\n';
          detailError += '- 确认字段名称符合规范\n';
          detailError += '- 检查是否有创建字段的权限';
        }
      }
      
      // 检查是否是匹配字段相关错误
      if (errorMessage.includes('匹配') || errorMessage.includes('match')) {
        detailError += '\n\n匹配字段问题：\n';
        detailError += '- 请确认选择的匹配字段在数据表中存在\n';
        detailError += '- 匹配字段的值不能为空\n';
        detailError += '- 匹配字段组合必须能唯一标识记录';
      }
      
      // 检查是否是数据格式错误
      if (errorMessage.includes('类型') || errorMessage.includes('type') || errorMessage.includes('格式')) {
        detailError += '\n\n数据类型问题：\n';
        detailError += '- 请检查字段映射中的数据类型\n';
        detailError += '- 确认导入数据格式与字段类型匹配\n';
        detailError += '- 数字字段不能包含文本\n';
        detailError += '- 日期字段格式需正确';
      }
      
      alert(detailError);
      setImportStatus(`导入失败: ${errorMessage}`);
      
      // 停止捕获日志
      stopLogCapture();
        restoreConsole();
    } finally {
      setLoading(false);
      // 无论导入成功、失败还是取消，都重置取消状态和导入标志，允许用户重新导入
      isImportCancelledRef.current = false;
      isImportingRef.current = false;
    }
  }

  // 预览表格列定义
  const previewColumns = parsedData?.headers.map(header => ({
    title: header,
    dataIndex: header,
    key: header,
    width: 150,
    render: (text: any) => {
      if (text === null || text === undefined || text === '') {
        return <span style={{ color: '#999' }}>空</span>;
      }
      return String(text);
    }
  })) || [];

  return (
    <div className="excel-import-plugin">
      <div className="plugin-header">
        <Typography.Title level={4}>Excel表格导入</Typography.Title>
      </div>

      <div className="plugin-content">
        {/* 导入来源选择 */}
        <Card title="选择导入来源" size="small" style={{ marginBottom: 16 }}>
          <Radio.Group
            value={importSource}
            onChange={(e) => setImportSource(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Radio value="local">
                <Typography.Text strong>从本地文件导入</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginLeft: 24 }}>
                  支持 Excel 格式（.xlsx, .xls）
                </Typography.Text>
              </Radio>
              
              <Radio value="dingtalk" disabled>
                <Typography.Text strong style={{ color: '#999' }}>从钉钉文档导入</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginLeft: 24, color: '#999' }}>
                  支持Excel文件（.xlsx, .xls）和钉钉在线表格（暂不可用：权限问题待解决）
                </Typography.Text>
              </Radio>
            </Space>
          </Radio.Group>
        </Card>
        
        {/* 文件上传区域 - 仅本地导入时显示 */}
        {importSource === 'local' && (
          <Card title="选择文件" size="small" style={{ marginBottom: 16 }}>
            <Upload
              accept=".xlsx,.xls"
              beforeUpload={(file) => {
                handleFileSelect(file);
                return false; // 阻止自动上传
              }}
              showUploadList={false}
            >
              <Button type="primary" loading={loading}>
                选择Excel文件
              </Button>
            </Upload>
            {file && (
              <div style={{ marginTop: 8 }}>
                <Typography.Text type="secondary">
                  已选择: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </Typography.Text>
              </div>
            )}
          </Card>
        )}
        
        {/* 钉钉文档导入 */}
        {importSource === 'dingtalk' && (
          <Card title="从钉钉文档导入" size="small" style={{ marginBottom: 16 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12, marginBottom: 12, display: 'block' }}>
              浏览钉钉文档目录，选择Excel文件导入
            </Typography.Text>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button 
                type="primary"
                loading={selectingDingtalkFile}
                onClick={handleSelectDingtalkFileOrSheet}
                disabled={loading}
                style={{ width: '100%' }}
              >
                {selectingDingtalkFile ? '正在下载文件...' : '浏览并选择文件'}
              </Button>
              {dingtalkFile && (
                <div style={{ marginTop: 8 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    已选择Excel文件: {dingtalkFile.fileName} {dingtalkFile.fileSize > 0 && ` (${(dingtalkFile.fileSize / 1024).toFixed(2)} KB)`}
                  </Typography.Text>
                </div>
              )}
              <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                支持：Excel文件（.xlsx, .xls）
              </Typography.Text>
            </Space>
          </Card>
        )}

        {/* 文件浏览器 */}
        <DingTalkFileBrowser
          visible={showFileBrowser}
          onCancel={() => setShowFileBrowser(false)}
          onSelect={handleFileBrowserSelect}
        />

        {/* 数据预览区域 */}
        {parsedData && (
          <Card title="数据预览（前3行）" size="small" style={{ marginBottom: 16 }}>
            <Typography.Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
              共 {parsedData.totalRows} 行，{parsedData.totalCols} 列
            </Typography.Text>
            <Table
              dataSource={previewRows}
              columns={previewColumns}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          </Card>
        )}

        {/* 导入规则配置 */}
        {parsedData && (
          <Card title="导入规则" size="small" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Checkbox
                checked={firstRowIsHeader}
                onChange={(e) => setFirstRowIsHeader(e.target.checked)}
              >
                第一行为字段名
              </Checkbox>
              
              <Checkbox
                checked={autoAddFields}
                onChange={(e) => setAutoAddFields(e.target.checked)}
              >
                自动添加不存在的字段名
              </Checkbox>
              
              <Checkbox
                checked={enableFuzzyMatch}
                onChange={(e) => setEnableFuzzyMatch(e.target.checked)}
              >
                启用字段模糊匹配（默认启用，不勾选时仅进行精确匹配）
              </Checkbox>
              
              <Checkbox
                checked={ignoreErrors}
                onChange={(e) => setIgnoreErrors(e.target.checked)}
              >
                忽略错误行（遇到错误时跳过该行继续导入）
              </Checkbox>
              
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                限制：最多 30000 行，300 列（超出部分将自动截断）
              </Typography.Text>
            </Space>
          </Card>
        )}

        {/* 字段映射复核界面 - 模式2-5显示 */}
        {parsedData && showFieldMapping && selectedSheetId && fields.length > 0 && (importMode === 'append' || importMode === 'updateBlank' || importMode === 'updateExisting' || importMode === 'updateBoth') && (
          <Card title="字段映射复核" size="small" style={{ marginBottom: 16 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12, marginBottom: 16, display: 'block' }}>
              请确认字段映射关系，选择"不导入"表示该字段不参与导入。
            </Typography.Text>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px',
              maxHeight: '400px', // 约10行，每行约40px
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: '8px'
            }}>
              {fieldMappings.map((mapping, index) => {
                const sourceOptions = [
                  { label: '不导入', value: '' },
                  ...parsedData.headers.map(h => ({ label: h, value: h }))
                ];
                
                return (
                  <div 
                    key={index} 
                    style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: '12px', 
                      alignItems: 'center',
                      padding: '8px',
                      border: '1px solid #f0f0f0',
                      borderRadius: '4px',
                        backgroundColor: mapping.isNewField
                          ? '#fff7e6'
                          : (mapping.isFuzzyMatch ? '#e6f7ff' : '#fafafa')
                    }}
                  >
                    {/* 左侧：数据表字段 */}
                    <div>
                      <Typography.Text 
                        style={{ 
                          fontSize: '13px',
                          color: mapping.isNewField ? '#fa8c16' : (mapping.isFuzzyMatch ? '#1890ff' : '#333'),
                          fontWeight: 500
                        }}
                      >
                        {mapping.targetField}
                        {mapping.isNewField && (
                          <span style={{ marginLeft: 4, color: '#fa8c16', fontSize: '12px' }}>(新增字段)</span>
                        )}
                        {!mapping.isNewField && mapping.isFuzzyMatch && (
                          <span style={{ marginLeft: 4, color: '#1890ff', fontSize: '12px' }}>(模糊匹配)</span>
                        )}
                      </Typography.Text>
                    </div>
                    
                    {/* 右侧：映射字段选择 */}
                    <div>
                      <Select
                        value={mapping.sourceField}
                        onChange={(value: string) => {
                          const newMappings = [...fieldMappings];
                          newMappings[index].sourceField = value;
                          // 用户手动调整后，将其视为确认匹配，取消模糊匹配高亮
                          newMappings[index].isFuzzyMatch = false;
                          setFieldMappings(newMappings);
                        }}
                        style={{ width: '100%' }}
                        options={sourceOptions}
                        placeholder="选择映射字段"
                        getPopupContainer={(triggerNode) => {
                          const container = document.querySelector('.excel-import-plugin') || document.body;
                          return container as HTMLElement;
                        }}
                        dropdownStyle={{ zIndex: 10000 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* 大数据量处理配置 */}
        {parsedData && parsedData.totalRows > 1000 && (
          <Card title="大数据量处理配置" size="small" style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                当前数据量：{parsedData.totalRows.toLocaleString()} 行，建议调整分片大小以优化性能
              </Typography.Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Typography.Text style={{ fontSize: 13, minWidth: 100 }}>分片大小：</Typography.Text>
                <Input
                  type="number"
                  value={chunkSize}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (value > 0 && value <= 10000) {
                      setChunkSize(value);
                    }
                  }}
                  style={{ width: 120 }}
                  min={100}
                  max={10000}
                  step={100}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  行/片（建议：1000-2000行）
                </Typography.Text>
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                预计分片数：{Math.ceil(parsedData.totalRows / chunkSize).toLocaleString()} 片
              </Typography.Text>
            </div>
          </Card>
        )}

        {/* 导入模式选择 */}
        {parsedData && (
          <Card title="导入模式" size="small" style={{ marginBottom: 16 }}>
            <Radio.Group
              value={importMode}
              onChange={(e) => {
                setImportMode(e.target.value);
              }}
              style={{ width: '100%' }}
            >
              <Space direction="vertical">
                <Radio value="create">
                  <div>
                    <Typography.Text strong>创建新表格（模式1）</Typography.Text>
                    {importMode === 'create' && (
                      <Input
                        placeholder="新表格名称（可选）"
                        value={newSheetName}
                        onChange={(e) => setNewSheetName(e.target.value)}
                        style={{ marginTop: 8, width: '100%' }}
                      />
                    )}
                  </div>
                </Radio>
                
                <Radio value="append">
                  <Typography.Text strong>在已有表格追加数据（模式2）</Typography.Text>
                </Radio>
                
                <Radio value="updateBlank">
                  <div>
                    <Typography.Text strong>基于选定字段只添加新建数据（模式3）</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                      仅添加数据表中不存在的新记录，已存在的记录跳过不处理。匹配字段：1-3个
                    </Typography.Text>
                  </div>
                </Radio>
                
                <Radio value="updateExisting">
                  <div>
                    <Typography.Text strong>基于选定字段只更新已有数据（模式4）</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                      仅更新数据表中已存在记录的非匹配字段，不添加新记录。匹配字段：1-3个
                    </Typography.Text>
                  </div>
                </Radio>
                
                <Radio value="updateBoth">
                  <div>
                    <Typography.Text strong>基于选定字段更新已有数据并添加新建数据（模式5）</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                      更新已存在记录的所有字段，并添加不存在的新记录。匹配字段：1-3个
                    </Typography.Text>
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </Card>
        )}

        {/* 模式2-5的配置选项 - 使用Card结构，不在Radio内部 */}
        {parsedData && (importMode === 'append' || importMode === 'updateBlank' || importMode === 'updateExisting' || importMode === 'updateBoth') && (
          <Card title="导入配置" size="small" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* 数据表选择 - 模式2-5都需要 */}
              <div>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                  选择数据表
                </Typography.Text>
                <Select
                  placeholder="选择数据表"
                  value={selectedSheetId}
                  onChange={handleSheetChange}
                  style={{ width: '100%' }}
                  options={sheetOptions}
                  notFoundContent={allSheets.length === 0 ? '暂无数据表，请先创建数据表' : '未找到数据表'}
                  getPopupContainer={(triggerNode) => {
                    const container = document.querySelector('.excel-import-plugin') || document.body;
                    return container as HTMLElement;
                  }}
                  dropdownStyle={{ zIndex: 10000 }}
                />
                {selectedSheetId && fields.length === 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                    正在加载字段...
                  </Typography.Text>
                )}
                {selectedSheetId && fields.length > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                    已选择数据表，共 {fields.length} 个字段
                  </Typography.Text>
                )}
              </div>

              {/* 视图选择 - 模式2-5都需要 */}
              {selectedSheetId && (
                <div>
                  <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                    选择视图
                  </Typography.Text>
                  <Select
                    placeholder="选择视图"
                    value={selectedViewId}
                    onChange={(value) => setSelectedViewId(value)}
                    style={{ width: '100%' }}
                    options={views.map(v => ({ label: v.name, value: v.id }))}
                    notFoundContent={views.length === 0 ? '暂无视图' : '未找到视图'}
                    getPopupContainer={(triggerNode) => {
                      const container = document.querySelector('.excel-import-plugin') || document.body;
                      return container as HTMLElement;
                    }}
                    dropdownStyle={{ zIndex: 10000 }}
                  />
                  {selectedViewId && views.length > 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                      已选择视图：{views.find(v => v.id === selectedViewId)?.name || selectedViewId}
                    </Typography.Text>
                  )}
                </div>
              )}

              {/* 匹配字段选择 - 模式3-5需要 */}
              {(importMode === 'updateBlank' || importMode === 'updateExisting' || importMode === 'updateBoth') && selectedSheetId && fields.length > 0 && (
                <div>
                  <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                    选择匹配字段（1-3个，只能选择已映射的导入字段）
                  </Typography.Text>
                  <Select
                    mode="multiple"
                    placeholder="选择匹配字段（1-3个，只能选择已映射的导入字段）"
                    value={matchFields}
                    onChange={(value) => {
                      // 限制最多选择3个字段
                      if (value.length <= 3) {
                        // 验证选择的字段是否在可用列表中
                        const availableFieldValues = matchFieldOptions.map(opt => opt.value);
                        const invalidFields = value.filter(field => !availableFieldValues.includes(field));
                        if (invalidFields.length > 0) {
                          alert(`以下字段不能作为匹配字段（未映射或选择了"不导入"）：${invalidFields.join('、')}`);
                          return;
                        }
                        setMatchFields(value);
                      } else {
                        alert('最多只能选择3个匹配字段');
                      }
                    }}
                    maxTagCount={3}
                    style={{ width: '100%' }}
                    options={matchFieldOptions}
                    notFoundContent={matchFieldOptions.length === 0 ? '没有可用的匹配字段，请先在字段映射中映射字段' : '未找到匹配字段'}
                    getPopupContainer={(triggerNode) => {
                      const container = document.querySelector('.excel-import-plugin') || document.body;
                      return container as HTMLElement;
                    }}
                    dropdownStyle={{ zIndex: 10000 }}
                  />
                  {matchFields.length > 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                      已选择 {matchFields.length} 个匹配字段：{matchFields.join('、')}
                    </Typography.Text>
                  )}
                  {matchFieldOptions.length === 0 && (
                    <Typography.Text type="warning" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                      ⚠️ 没有可用的匹配字段，请先在字段映射中映射字段（不能选择"不导入"的字段）
                    </Typography.Text>
                  )}
                </div>
              )}
            </Space>
          </Card>
        )}

        {/* 重复数据扫描结果和操作按钮 - 不加载详细数据，只显示统计信息 */}
        {duplicateData && duplicateData.duplicateGroups && duplicateData.duplicateGroups.length > 0 && (() => {
          // 此时 duplicateData 已确定不为 null
          const data = duplicateData;
          
          // 计算所有非最佳记录的ID（用于删除操作）
          const allNonBestRecordIds: string[] = [];
          data.duplicateGroups.forEach(group => {
            if (group && group.records && group.records.length > 0) {
              group.records.forEach((record: any) => {
                if (record && record.id && !record.isBestRecord) {
                  allNonBestRecordIds.push(record.id);
                }
              });
            }
          });
          
          return (
            <Card 
              size="small" 
              style={{ marginBottom: 16, borderColor: '#ff9800', borderWidth: 1, borderStyle: 'solid' }}
            >
              <Typography.Text strong style={{ fontSize: 14, display: 'block', marginBottom: 16, color: '#ff9800' }}>
                ⚠️ 发现重复数据，请选择操作
              </Typography.Text>
              
              {/* 统计信息 */}
              <div style={{ marginBottom: 16 }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Typography.Text strong style={{ fontSize: 13, minWidth: 80 }}>
                      数据表：
                    </Typography.Text>
                    <Typography.Text style={{ fontSize: 12 }}>
                      {data.sheetName || '未知'}
                    </Typography.Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Typography.Text strong style={{ fontSize: 13, minWidth: 80 }}>
                      查找字段：
                    </Typography.Text>
                    <Space wrap>
                      {data.queryFields && data.queryFields.length > 0 ? (
                        data.queryFields.map((f, idx) => (
                          <Typography.Text 
                            key={idx}
                            style={{ 
                              fontSize: 12, 
                              padding: '2px 8px',
                              backgroundColor: '#f0f0f0',
                              borderRadius: 4
                            }}
                          >
                            {f && f.name ? f.name : '未知'}
                          </Typography.Text>
                        ))
                      ) : (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>未知</Typography.Text>
                      )}
                    </Space>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Typography.Text strong style={{ fontSize: 13, minWidth: 80 }}>
                      总记录数：
                    </Typography.Text>
                    <Typography.Text style={{ fontSize: 12 }}>
                      {data.totalRecords ? data.totalRecords.toLocaleString() : 0} 条
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      （查询 {data.totalQueries || 0} 次）
                    </Typography.Text>
                  </div>
                  <div style={{ 
                    padding: '12px 16px', 
                    backgroundColor: '#fff7e6', 
                    borderRadius: 4,
                    border: '1px solid #ffd591'
                  }}>
                    <Typography.Text type="warning" style={{ fontSize: 14, fontWeight: 500 }}>
                      发现 <Typography.Text strong style={{ color: '#fa8c16' }}>{data.duplicateGroups.length.toLocaleString()}</Typography.Text> 组重复数据，
                      共 <Typography.Text strong style={{ color: '#fa8c16' }}>{(data.duplicateCount || 0).toLocaleString()}</Typography.Text> 条重复记录
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                      系统已自动选择最佳记录保留，可删除 {allNonBestRecordIds.length.toLocaleString()} 条重复记录
                    </Typography.Text>
                  </div>
                </Space>
              </div>
              
              {/* 操作按钮 */}
              <Space>
                <Button
                  type="primary"
                  danger
                  size="large"
                  onClick={async () => {
                    if (!selectedSheetId) {
                      alert('数据表ID不存在');
                      return;
                    }
                    
                    if (allNonBestRecordIds.length === 0) {
                      alert('没有可删除的重复记录');
                      return;
                    }
                    
                    const confirmMessage = `确定要删除 ${allNonBestRecordIds.length.toLocaleString()} 条重复记录吗？\n\n此操作不可撤销！\n\n系统将保留每组重复数据中的最佳记录，删除其余记录。`;
                    
                    if (!window.confirm(confirmMessage)) {
                      return;
                    }
                    
                    try {
                      setLoading(true);
                      
                      // 使用节流更新状态，减少UI渲染频率
                      let lastUpdateTime = 0;
                      const updateStatusThrottled = (message: string, force: boolean = false) => {
                        const now = Date.now();
                        if (force || now - lastUpdateTime >= 1000) { // 最多每秒更新一次
                          setImportStatus(message);
                          lastUpdateTime = now;
                        }
                      };
                      
                      updateStatusThrottled(`正在删除 ${allNonBestRecordIds.length.toLocaleString()} 条重复记录...`, true);
                      
                      // 增加超时时间，因为现在有延迟
                      // 估算：每条记录平均需要 200-400ms，加上延迟，10000条大约需要 30-60分钟
                      const estimatedMinutes = Math.ceil(allNonBestRecordIds.length / 1000 * 2); // 每1000条约2分钟
                      const timeoutSeconds = Math.max(600, estimatedMinutes * 60); // 至少10分钟，或按估算时间
                      
                      const deleteResult = await scriptRunWithTimeout('deleteSelectedDuplicateRecords', timeoutSeconds,
                        selectedSheetId,
                        allNonBestRecordIds
                      );
                      
                      console.log('删除结果:', deleteResult);
                      
                      // 显示删除结果
                      let statusMessage = `删除完成，已删除 ${deleteResult.deletedCount.toLocaleString()} 条记录`;
                      if (deleteResult.errors && deleteResult.errors.length > 0) {
                        statusMessage += `，${deleteResult.errors.length} 条记录删除失败`;
                        console.warn('删除失败的记录:', deleteResult.errors);
                      }
                      
                      setImportStatus(statusMessage);
                      
                      // 清空重复数据状态
                      setDuplicateData(null);
                      
                      // 等待一下让用户看到结果
                      await new Promise(resolve => setTimeout(resolve, 1500));
                      
                      // 如果有失败记录，询问用户是否继续
                      if (deleteResult.errors && deleteResult.errors.length > 0) {
                        const shouldContinue = window.confirm(
                          `删除完成，但有 ${deleteResult.errors.length} 条记录删除失败。\n\n是否继续导入？`
                        );
                        if (!shouldContinue) {
                          setLoading(false);
                          return;
                        }
                      }
                      
                      // 自动继续导入
                      await handleContinueImport();
                    } catch (err: any) {
                      console.error('删除重复记录失败:', err);
                      
                      // 检查是否是超时错误
                      if (err.message && err.message.includes('timeout')) {
                        alert(`删除操作超时。\n\n已删除的记录可能已保存，请检查数据表状态。\n\n如果部分记录已删除，您可以稍后重新运行删除操作。`);
                      } else {
                        alert(`删除失败: ${err.message}`);
                      }
                      
                      setLoading(false);
                    }
                  }}
                  disabled={loading || allNonBestRecordIds.length === 0}
                >
                  删除所有重复数据（{allNonBestRecordIds.length.toLocaleString()} 条）
                </Button>
                <Button
                  size="large"
                  onClick={handleContinueImport}
                  disabled={loading}
                >
                  跳过，继续导入
                </Button>
              </Space>
            </Card>
          );
        })()}

        {/* 导入进度条 */}
        {(importProgress > 0 || queryProgress.isQuerying) && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Progress percent={importProgress} />
            {queryProgress.isQuerying && (
              <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                正在查询数据
              </Typography.Text>
            )}
            {importProgress > 0 && !queryProgress.isQuerying && (
              <>
                {importStatus && (
                  <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                    {importStatus}
                  </Typography.Text>
                )}
                {totalChunks > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                    分段进度：第 {currentChunk} / {totalChunks} 段
                  </Typography.Text>
                )}
              </>
            )}
          </Card>
        )}

        {/* 合并选项配置 - 独立Card，放在导入按钮前 */}
        {parsedData && (importMode === 'append' || importMode === 'updateBlank' || importMode === 'updateExisting' || importMode === 'updateBoth') && (
          <Card title="合并选项" size="small" style={{ marginBottom: 16, marginTop: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Checkbox
                checked={enableAggregation}
                onChange={(e) => setEnableAggregation(e.target.checked)}
              >
                汇总导入（按字段合并数据）
              </Checkbox>
              
              {/* 重复数据处理（仅在未启用汇总导入时显示） */}
              {!enableAggregation && (
                <div>
                  <Checkbox
                    checked={enableDuplicateMerge}
                    onChange={(e) => setEnableDuplicateMerge(e.target.checked)}
                  >
                    合并重复数据（当导入数据中存在相同匹配键的多条记录时）
                  </Checkbox>
                  
                  {/* 重复数据处理策略（仅在启用合并且已选择匹配字段时显示） */}
                  {enableDuplicateMerge && matchFields.length > 0 && (
                    <div style={{ marginLeft: 24, marginTop: 8, padding: 12, backgroundColor: '#f0f7ff', borderRadius: 4 }}>
                      <Space direction="vertical" style={{ width: '100%' }} size="small">
                        <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                          合并策略：
                        </Typography.Text>
                        <Select
                          value={duplicateMergeStrategy}
                          onChange={(value) => setDuplicateMergeStrategy(value)}
                          style={{ width: '100%' }}
                          options={[
                            { label: '保留字段完整度更高的记录', value: 'mostComplete' },
                            { label: '保留最早创建的', value: 'earliest' },
                            { label: '保留最近创建的', value: 'latest' },
                            { label: '保留最近编辑过的', value: 'mostRecentEdit' },
                            { label: '保留较旧的编辑', value: 'oldestEdit' },
                            { label: '保留自定义字段较大的记录', value: 'largestField' },
                            { label: '保留自定义字段较小的记录', value: 'smallestField' }
                          ]}
                        />
                        {(duplicateMergeStrategy === 'largestField' || duplicateMergeStrategy === 'smallestField') && (
                          <Select
                            placeholder="选择用于比较的自定义字段"
                            value={duplicateMergeField}
                            onChange={(value) => setDuplicateMergeField(value)}
                            style={{ width: '100%' }}
                            options={parsedData.headers.map(h => ({ label: h, value: h }))}
                          />
                        )}
                      </Space>
                    </div>
                  )}
                  {enableDuplicateMerge && matchFields.length === 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 24, marginTop: 4, display: 'block' }}>
                      请先选择匹配字段（模式3-5需要）
                    </Typography.Text>
                  )}
                </div>
              )}
              
              {enableAggregation && (
                <div style={{ marginLeft: 24, marginTop: 8, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <div>
                      <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                        分组字段（1-3个，所有分组字段值都相同的记录将合并）：
                      </Typography.Text>
                      <Select
                        mode="multiple"
                        placeholder="选择分组字段"
                        value={aggregationFields}
                        onChange={(values) => {
                          if (values.length <= 3) {
                            setAggregationFields(values);
                          } else {
                            alert('分组字段最多只能选择3个');
                          }
                        }}
                        style={{ width: '100%' }}
                        options={parsedData.headers.map(h => ({ label: h, value: h }))}
                        maxTagCount={3}
                      />
                    </div>
                    
                    <div>
                      <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                        数值字段（将进行求和）：
                      </Typography.Text>
                      <Select
                        mode="multiple"
                        placeholder="选择需要求和的数值字段（可选）"
                        value={sumFields}
                        onChange={setSumFields}
                        style={{ width: '100%' }}
                        options={parsedData.headers.map(h => ({ label: h, value: h }))}
                      />
                    </div>
                    
                    <div>
                      <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                        文本合并分隔符：
                      </Typography.Text>
                      <Input
                        value={textSeparator}
                        onChange={(e) => setTextSeparator(e.target.value)}
                        placeholder="默认：；"
                        style={{ width: '100%' }}
                      />
                    </div>
                  </Space>
                </div>
              )}
            </Space>
          </Card>
        )}

        {/* 确认导入按钮和停止按钮 */}
        {parsedData && (
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Space>
              <Space align="center">
                <Typography.Text>从第几批开始导入</Typography.Text>
                <Input
                  type="number"
                  min={1}
                  value={startChunk}
                  onChange={(e) => setStartChunk(Number(e.target.value) || 1)}
                  style={{ width: 120 }}
                  placeholder="默认1"
                />
              </Space>
            <Button
              type="primary"
              size="large"
              loading={loading}
              onClick={handleImport}
                disabled={!parsedData || loading}
            >
              确认导入
            </Button>
              {loading && (
                <Button
                  type="default"
                  size="large"
                  danger
                  onClick={handleStopImport}
                >
                  停止导入
                </Button>
              )}
            </Space>
          </div>
        )}

      </div>
    </div>
  );
}

export default App;

